/**
 * Chase List Uploader Component
 *
 * Handles the full media plan upload flow:
 * 1. File selection (Excel/CSV)
 * 2. Sheet selection (for multi-sheet Excel files)
 * 3. AI-powered parsing
 * 4. Human review/edit with org linking
 * 5. Save to Firestore as a Job
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Spinner,
  Table,
  Progress,
} from 'reactstrap';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox';
import { makeStyles } from '@material-ui/core/styles';
import { useOrganizationStore } from '../../store/organization.store';
import { useCollectionCache } from '../../services/collectionCache.service';
import {
  readExcelFile,
  readCSVFile,
  extractSheetData,
  extractMultipleSheets,
  parseChaseListWithAI,
  SheetExtractionResult,
} from '../../services/chaseListParser.service';
import { ParsedChaseList, ParsedStationEntry, LinkedStationEntry } from '../../prompts/chaseListParsingPrompt';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { parseFlightingCode, normaliseFlightingCode, normaliseFlightingCodeBatch, isLiveReadCode } from '../../utils/flightingCode';
import { normaliseDeadlineDate, normaliseCampaignPeriod } from '../../utils/chaseListDate';
import { useToast } from '../../contexts/ToastContext';
import { computeCampaignDiff, CampaignChange } from '../../utils/campaignHistory';
import { ExistingCampaignForMerge } from '../../prompts/chaseListParsingPrompt';

// Language abbreviation mapping for chase list parsing
const LANGUAGE_ALIASES: Record<string, string> = {
  eng: 'english',
  afr: 'afrikaans',
  afk: 'afrikaans',
  zul: 'zulu',
  xho: 'xhosa',
  sot: 'sotho',
  sth: 'sotho',
  tsw: 'tswana',
  ped: 'pedi',
  sep: 'pedi',
  ven: 'venda',
  tso: 'tsonga',
  swa: 'swazi',
  ssw: 'swazi',
  nde: 'ndebele',
  nbl: 'ndebele',
  por: 'portuguese',
  fre: 'french',
  fra: 'french',
  english: 'english',
  afrikaans: 'afrikaans',
  isizulu: 'zulu',
  isixhosa: 'xhosa',
  sesotho: 'sotho',
  setswana: 'tswana',
  sepedi: 'pedi',
  tshivenda: 'venda',
  xitsonga: 'tsonga',
  siswati: 'swazi',
  isindebele: 'ndebele',
  'n sotho': 'pedi',
  'northern sotho': 'pedi',
  'south sotho': 'sotho',
  'southern sotho': 'sotho',
  zulu: 'zulu',
  xhosa: 'xhosa',
  sotho: 'sotho',
  tswana: 'tswana',
  pedi: 'pedi',
  venda: 'venda',
  tsonga: 'tsonga',
  swazi: 'swazi',
  ndebele: 'ndebele',
  portuguese: 'portuguese',
  french: 'french',
};

// Language options (same as AddAudioForm)
const LANGUAGE_OPTIONS = [
  { label: 'English', value: 'english' },
  { label: 'Afrikaans', value: 'afrikaans' },
  { label: 'Zulu', value: 'zulu' },
  { label: 'Xhosa', value: 'xhosa' },
  { label: 'Sotho', value: 'sotho' },
  { label: 'Tswana', value: 'tswana' },
  { label: 'Pedi', value: 'pedi' },
  { label: 'Venda', value: 'venda' },
  { label: 'Tsonga', value: 'tsonga' },
  { label: 'Swazi', value: 'swazi' },
  { label: 'Ndebele', value: 'ndebele' },
  { label: 'Portuguese', value: 'portuguese' },
  { label: 'French', value: 'french' },
  { label: 'Other', value: 'other' },
];

/**
 * Special-case station → media-owner overrides. Applied by
 * `toLinkedStations` when the AI didn't return a media owner AND the
 * broadcaster's parent-inference didn't fire (e.g. the station isn't
 * a linked broadcaster in the global cache).
 *
 * Keys are LOWERCASED, whitespace-trimmed station names. Values are
 * the canonical media-owner NAME (looked up at runtime in the
 * mediaowner cache so an org id change or rename fails visibly rather
 * than silently misrouting).
 *
 * Add entries here when a station consistently ships without a media
 * house on chase lists but should always route to a specific one. The
 * cleaner long-term fix is to add the station as a linked broadcaster
 * with `parentMediaOwnerOrgId` set — that removes the special case
 * from code entirely.
 */
const STATION_MEDIA_OWNER_OVERRIDES: Record<string, string> = {
  'radio retail': 'ZAPOP',
};

interface GlobalEntry {
  id: string;
  name: string;
  linkedOrgId: string | null;
  /** Broadcasters only: parent media owner's org doc id (matches mediaowner.linkedOrgId). */
  parentMediaOwnerOrgId?: string | null;
  parentMediaOwnerName?: string | null;
  /** Broadcasters only: 'public' | 'commercial' | 'community' | 'campus' */
  subType?: string | null;
  /** Broadcasters only: SA province name or 'national' */
  province?: string | null;
  /** Broadcasters only: LANGUAGE_OPTIONS values they accept ([] = no restriction) */
  acceptedLanguages?: string[];
}

// Filter values for the "Add stations by filter" bulk picker.
const FILTER_PROVINCES = [
  'Gauteng',
  'Mpumalanga',
  'Limpopo',
  'Free State',
  'North West',
  'KwaZulu-Natal',
  'Eastern Cape',
  'Northern Cape',
  'Western Cape',
  'National',
];
const FILTER_SUBTYPES = [
  { value: 'public',     label: 'Public' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'community',  label: 'Community' },
  { value: 'campus',     label: 'Campus' },
];

const useStyles = makeStyles({
  autocomplete: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      color: '#fff',
      fontSize: '0.8rem',
      minHeight: '28px',
      padding: '0 4px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: '0.8rem', padding: '2px 4px !important' },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': { color: 'rgba(255,255,255,0.3)', padding: '2px' },
    '& .MuiChip-root': { height: '20px', fontSize: '0.7rem', backgroundColor: 'var(--accent-1-color)', color: '#fff' },
    '& .MuiChip-deleteIcon': { color: '#fff', fontSize: '14px' },
  },
});

interface ChaseListUploaderProps {
  isOpen: boolean;
  onClose: () => void;
  firebase: any;
  onSaved?: (jobId: string) => void;
  editJob?: {
    id: string;
    header: any;
    stations: any[];
    /** Current lifecycle status of the job — 'draft' | 'active' |
     *  'delivered'. Required so the save-path's "preserve status when
     *  the diff reports no invalidating changes" fallback works. If
     *  omitted, the save unconditionally bounces the campaign to draft
     *  even on innocuous header edits (add / remove agency, fix a
     *  typo). Callers MUST pass this through from the loaded job doc. */
    status?: string;
    /** Set true when a station-field edit has landed on a delivered
     *  campaign since the last publish. Signals to the CTA that a
     *  "Publish Updates" action is needed to push the edits into
     *  broadcaster delivery records + copies. Independent of `status`
     *  (which stays 'delivered' throughout — the snapshot is still
     *  live, just out of date). */
    hasPendingUpdates?: boolean;
    /** Set when this is a campaign copy — the save path will redirect the
     *  update upstream to the source doc so all copies stay in sync via
     *  syncCampaignCopies. Absent for source jobs. */
    isCampaignCopy?: boolean;
    sourceOrgId?: string;
    sourceJobId?: string;
    /** Role of the current org on this campaign, used to stamp the history
     *  entry with the acting party's relationship to the campaign. */
    copyRole?: 'studio' | 'agency' | 'creator' | null;
    /** Original upload fields carried through so re-tagged copies get a
     *  faithful mirror. All optional — the syncCampaignCopies call filters
     *  out anything undefined. */
    fileName?: string;
    sheetName?: string;
    uploadedAt?: number;
    uploadedBy?: string;
    uploadedByName?: string;
  } | null;
}

type Step = 'upload' | 'select-sheet' | 'parsing' | 'review' | 'saving';

const ChaseListUploader: React.FC<ChaseListUploaderProps> = ({
  isOpen,
  onClose,
  firebase,
  onSaved,
  editJob,
}) => {
  const classes = useStyles();
  const { showSuccess } = useToast();
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  // Multi-sheet selection: chase lists sometimes split station rows
  // across sheets (regional splits, community vs metro, etc.). The user
  // ticks the sheets that all belong to the same campaign and the
  // parser concatenates them into one payload. Retains the legacy
  // single-sheet behaviour when only one sheet is ticked. `selectedSheet`
  // (singular) below is preserved as the primary display label — set
  // to the joined list for multi-sheet parses.
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [parsedData, setParsedData] = useState<ParsedChaseList | null>(null);
  const [editedStations, setEditedStations] = useState<LinkedStationEntry[]>([]);
  // Track whether the user reached the review step via the "Create Manually"
  // flow vs. an AI parse, so the modal header reflects what the user actually
  // did instead of always saying "Review Parsed Data".
  const [isManualMode, setIsManualMode] = useState(false);
  // Broadcaster + mediaowner lookups come straight from the global cache
  // (loaded once at app boot via collectionCache.loadEssentialCollections),
  // so opening this modal no longer triggers a Firestore round-trip.
  // Reshaping into the GlobalEntry contract keeps the rest of the component
  // unchanged.
  const cachedBroadcaster = useCollectionCache((s) => s.broadcaster);
  const cachedMediaowner = useCollectionCache((s) => s.mediaowner);
  // Studio + agency lookups for the new header-level matching. Same cache
  // pattern as broadcaster/mediaowner — loaded once at app boot. Used by
  // the AI parser (passed in as lookup tables) AND by the review-step UI
  // for manual override.
  const cachedStudioOrgs = useCollectionCache((s) => s.studioOrgs);
  const cachedAgencyOrgs = useCollectionCache((s) => s.agency);
  const stationOptions: GlobalEntry[] = useMemo(() => (
    cachedBroadcaster
      .map((d: any) => ({
        id: d.id,
        name: d.displayName || d.label || d.name || '',
        linkedOrgId: d.linkedOrgId || null,
        parentMediaOwnerOrgId: d.parentMediaOwnerOrgId || null,
        parentMediaOwnerName: d.parentMediaOwnerName || null,
        subType: d.subType || null,
        province: d.province || null,
        acceptedLanguages: Array.isArray(d.acceptedLanguages) ? d.acceptedLanguages : [],
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [cachedBroadcaster]);
  const mediaOwnerOptions: GlobalEntry[] = useMemo(() => (
    cachedMediaowner
      .map((d: any) => ({
        id: d.id,
        name: d.displayName || d.label || d.name || '',
        linkedOrgId: d.linkedOrgId || null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [cachedMediaowner]);
  // Studio + agency options use the lightweight { id: linkedOrgId, name }
  // shape since the only field we care about downstream is the linkedOrgId
  // for routing campaign + audiometa copies on save / auto-match.
  const studioOptions = useMemo(() => (
    cachedStudioOrgs
      .filter((d: any) => d.linkedOrgId)
      .map((d: any) => ({ id: d.linkedOrgId as string, name: d.displayName || d.label || d.name || '' }))
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [cachedStudioOrgs]);
  const agencyOptions = useMemo(() => (
    cachedAgencyOrgs
      .filter((d: any) => d.linkedOrgId)
      .map((d: any) => ({ id: d.linkedOrgId as string, name: d.displayName || d.label || d.name || '' }))
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [cachedAgencyOrgs]);
  // Clients still go through a per-mount fetch because resolveClient can
  // grow this list locally when a chase list contains a brand-new client
  // name. TODO: move /client to the same cache + use refreshGlobalEntries
  // after addDoc.
  const [clientOptions, setClientOptions] = useState<{ label: string; value: string }[]>([]);
  const [hiddenRowWarning, setHiddenRowWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Staged save progress — drives the progress bar in the footer while
  // handleSave runs (job write → linked-spot propagation → finishing).
  const [saveStage, setSaveStage] = useState<{ label: string; pct: number } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Separate file input for the merge flow so we don't conflict with the
  // initial upload input. The merge context ref is set BEFORE the file
  // input is triggered so handleParse can pick it up when it runs.
  const mergeFileInputRef = useRef<HTMLInputElement>(null);
  const mergeContextRef = useRef<ExistingCampaignForMerge | null>(null);
  const { currentOrgId } = useOrganizationStore();

  // Per-row banner state for "broadcaster's parent doesn't match the selected
  // media owner" — populated on station pick, dismissed via Yes / Cancel.
  const [mediaOwnerMismatch, setMediaOwnerMismatch] = useState<
    Record<number, { parentOrgId: string; parentName: string } | undefined>
  >({});

  // Bulk "Add stations by filter" modal state.
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterProvinces, setFilterProvinces] = useState<string[]>([]);
  const [filterSubTypes, setFilterSubTypes] = useState<string[]>([]);

  // Per-row "view / edit description" modal state. Description cells are
  // collapsed to single-line so the row height stays consistent with the
  // other fields; this modal opens a proper multi-line textarea for the
  // full brief. `idx` is the row index being edited; null = closed.
  const [editingDescriptionIdx, setEditingDescriptionIdx] = useState<number | null>(null);

  // Phase 2 delete-cascade confirmation modal. When the user removes one
  // or more already-delivered stations from a campaign, handleSave awaits
  // a promise here — the user clicks Continue or Cancel and the promise
  // resolves boolean. Using a promise (rather than a callback) keeps
  // handleSave's linear async flow readable.
  const [deleteConfirm, setDeleteConfirm] = useState<{
    stations: any[];
    resolve: (ok: boolean) => void;
  } | null>(null);
  const confirmDeletedDelivered = (stations: any[]): Promise<boolean> =>
    new Promise((resolve) => setDeleteConfirm({ stations, resolve }));

  // Studio + agency both-blank confirmation. Only fires on NEW campaign
  // saves — editing an existing job doesn't trigger it (legacy campaigns
  // may legitimately have both blank). Resolves `true` when the user
  // clicks Skip (proceed with blanks) or `false` when they click Add
  // (abort save so they can fill in). If they add only ONE of the two
  // and save again, the check no longer fires — presence of either
  // field signals intent.
  const [studioAgencyConfirm, setStudioAgencyConfirm] = useState<{
    resolve: (skip: boolean) => void;
  } | null>(null);
  const confirmMissingStudioAgency = (): Promise<boolean> =>
    new Promise((resolve) => setStudioAgencyConfirm({ resolve }));

  // Unlinked-orgs save warning. Fires on BOTH create and edit saves when
  // any station has a broadcaster / media-house NAME typed but no linked
  // in-app org (OrgId absent). Such rows only ever receive an external
  // delivery link — no in-app delivery / library copy happens. Resolves
  // `true` to proceed anyway, `false` to go back and fix the links.
  // `rows` carries { station, mediaOwner, missingStation, missingMediaOwner }
  // so the modal can list exactly which cell is unlinked.
  const [unlinkedConfirm, setUnlinkedConfirm] = useState<{
    rows: { station: string; mediaOwner: string; missingStation: boolean; missingMediaOwner: boolean }[];
    resolve: (proceed: boolean) => void;
  } | null>(null);
  const confirmUnlinkedOrgs = (
    rows: { station: string; mediaOwner: string; missingStation: boolean; missingMediaOwner: boolean }[],
  ): Promise<boolean> =>
    new Promise((resolve) => setUnlinkedConfirm({ rows, resolve }));

  // Re-deliver-corrected-station confirmation (Option A). Fires on EDIT
  // saves when an already-`delivered` station's broadcaster / media-house
  // org link changed. Confirming withdraws the previous (stale/incorrect)
  // delivery via cascadeDeleteStations and resets those stations to
  // `pending` so the existing Deliver CTA can re-ship them to the corrected
  // recipient. Resolves `true` to proceed, `false` to abort the save.
  const [redeliverConfirm, setRedeliverConfirm] = useState<{
    stations: any[];
    resolve: (ok: boolean) => void;
  } | null>(null);
  const confirmRedeliverCorrected = (stations: any[]): Promise<boolean> =>
    new Promise((resolve) => setRedeliverConfirm({ stations, resolve }));

  // Sticky-sync map for the "apply to all" checkboxes on row 0's
  // flighting code / deadline cells. While a field is enabled in this map,
  // edits to row 0 for that field mirror down to every other row. The
  // flighting code mirror also re-runs the flighting-code parser per row to
  // refresh duration + language.
  const APPLY_TO_ALL_FIELDS = ['flightingCode', 'materialDeadline'] as const;
  type ApplyToAllField = (typeof APPLY_TO_ALL_FIELDS)[number];
  const [applyToAll, setApplyToAll] = useState<Record<ApplyToAllField, boolean>>({
    flightingCode: false,
    materialDeadline: false,
  });

  // Load /client only — broadcaster + mediaowner come from the global
  // cache (see stationOptions / mediaOwnerOptions memos above), so no
  // fetch is needed for them on modal open.
  useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !firebase) return;
      try {
        const clientsSnapshot = await getDocs(collection(firebase.db, 'client'));
        const clients: { label: string; value: string }[] = [];
        clientsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          clients.push({ label: data.label || data.name || '', value: data.value || doc.id });
        });
        clients.sort((a, b) => a.label.localeCompare(b.label));
        setClientOptions(clients);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    loadData();
  }, [isOpen, firebase]);

  // Populate form when editing an existing job.
  //
  // CRITICAL: only depend on the STABLE job id, not the editJob object
  // reference. JobDetail constructs a fresh `editJob={{...}}` literal on
  // every render, so a dependency of `[isOpen, editJob]` would fire this
  // effect on every parent re-render (e.g. a Firestore subscription tick)
  // — wiping the current editor state (including in-flight merges) back
  // to whatever's in the source doc. Locking to the id means the effect
  // only fires when we actually open a different job.
  useEffect(() => {
    if (isOpen && editJob) {
      const header = editJob.header || { client: '', campaign: '', period: '', date: '', revision: '', agencyContacts: [], studioOrgId: '', studioName: '', agencyOrgId: '', agencyName: '' };
      if (header.client == null) header.client = '';
      // Defensive defaults for the new studio + agency fields when editing
      // a campaign that pre-dates them.
      if ((header as any).studioOrgId == null) (header as any).studioOrgId = '';
      if ((header as any).studioName == null) (header as any).studioName = '';
      if ((header as any).agencyOrgId == null) (header as any).agencyOrgId = '';
      if ((header as any).agencyName == null) (header as any).agencyName = '';
      setParsedData({
        header,
        stations: editJob.stations || [],
      });
      setEditedStations(editJob.stations || []);
      setStep('review');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editJob?.id]);

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setWorkbook(null);
    setSheetNames([]);
    setSelectedSheet('');
    setParsedData(null);
    setEditedStations([]);
    setIsManualMode(false);
    setError(null);
    setSaving(false);
    setSaveStage(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Convert AI-parsed stations to linked station entries
  const toLinkedStations = (stations: ParsedStationEntry[]): LinkedStationEntry[] => {
    return stations.map(s => {
      // Prefer the AI's org match (semantic — handles abbreviations,
      // missing suffixes, regional variants). Falls back to exact name
      // match for safety when the AI returns no id but the chase-list
      // string happens to be identical to a canonical name.
      const aiStation = (s as any).stationOrgId
        ? stationOptions.find((entry) => entry.linkedOrgId === (s as any).stationOrgId)
        : null;
      const matchedStation = aiStation || stationOptions.find(
        (entry: GlobalEntry) => entry.name.toLowerCase() === s.station.toLowerCase()
      );
      const aiOwner = (s as any).mediaOwnerOrgId
        ? mediaOwnerOptions.find((entry) => entry.linkedOrgId === (s as any).mediaOwnerOrgId)
        : null;
      const nameMatchedOwner = mediaOwnerOptions.find(
        (entry: GlobalEntry) => entry.name.toLowerCase() === s.mediaOwner.toLowerCase()
      );
      // Special-case station → media-owner overrides. Applied when the
      // AI didn't provide a media owner value (aiOwner + nameMatchedOwner
      // both null) but the station name matches a known override.
      // Looked up by NAME in mediaOwnerOptions at runtime, so an org
      // rename doesn't silently misroute — the mapping just no-ops until
      // the name matches again. If a new special case comes up, add a
      // row to STATION_MEDIA_OWNER_OVERRIDES; no other code change.
      const stationKey = String(s.station || '').trim().toLowerCase();
      const overrideMediaOwnerName = STATION_MEDIA_OWNER_OVERRIDES[stationKey];
      const overrideOwner = overrideMediaOwnerName
        ? mediaOwnerOptions.find(
            (entry: GlobalEntry) => entry.name.trim().toLowerCase() === overrideMediaOwnerName.toLowerCase(),
          )
        : null;
      // Fallback: if the chase list didn't list a media owner (or the AI
      // couldn't resolve one), infer it from the matched broadcaster's
      // parent org. Mirrors the manual auto-fill in handleStationOrgSelect
      // so a broadcaster-only chase list still populates the media house.
      const parentInferredOwner = matchedStation?.parentMediaOwnerOrgId
        ? {
            id: '',
            name: matchedStation.parentMediaOwnerName || '',
            linkedOrgId: matchedStation.parentMediaOwnerOrgId,
          } as GlobalEntry
        : null;
      // Priority: source data > explicit override > broadcaster parent.
      const matchedOwner = aiOwner || nameMatchedOwner || overrideOwner || parentInferredOwner;

      // Parse language string into options — resolve abbreviations
      const langParts = s.language.split(/[,&]+/).map(l => l.trim().toLowerCase());
      const languageOptions = langParts
        .map(lp => {
          // First try direct match
          const direct = LANGUAGE_OPTIONS.find(lo => lo.label.toLowerCase() === lp || lo.value === lp);
          if (direct) return direct;
          // Try abbreviation alias
          const aliasValue = LANGUAGE_ALIASES[lp];
          if (aliasValue) return LANGUAGE_OPTIONS.find(lo => lo.value === aliasValue);
          return null;
        })
        .filter(Boolean) as { label: string; value: string }[];

      // Flighting code is the source of truth for duration + language.
      // If parseable, override whatever the AI extracted from the chase list.
      const codeParts = parseFlightingCode(s.flightingCode);
      const finalDuration = codeParts.duration ?? s.duration;
      const finalLanguageOptions = codeParts.language
        ? [codeParts.language]
        : languageOptions;
      const finalLanguage = codeParts.language
        ? codeParts.language.label
        : s.language;

      // Display text for the media-owner Autocomplete is bound to
      // `mediaOwner` (the free-text field), not `mediaOwnerOrgName`. When
      // the fallback resolved the owner from the broadcaster's parent, the
      // chase-list string is empty, so we mirror the canonical name into
      // `mediaOwner` here — matches the manual autofill in
      // handleStationOrgSelect which sets all three.
      const finalMediaOwner = s.mediaOwner || matchedOwner?.name || '';

      // Chase lists arrive with wildly inconsistent deadline formats
      // ("5/11", "5 Nov", "5th November", "Nov 5", "2026-11-05"). Normalise
      // to `DD MMM YYYY` so the review table shows one canonical shape.
      // When the year is missing, current year is assumed; if that date is
      // already in the past, the year rolls forward. See
      // src/utils/chaseListDate.ts for the full logic.
      const finalDeadline = normaliseDeadlineDate(s.materialDeadline);

      // Live-read detection from the flighting-code suffix (LR / L_R / L).
      // Live reads have no audio to link — auto-match skips them and the
      // delivery gate treats them as ready.
      const liveRead = isLiveReadCode(s.flightingCode);

      return {
        ...s,
        mediaOwner: finalMediaOwner,
        materialDeadline: finalDeadline,
        duration: finalDuration,
        language: finalLanguage,
        isLiveRead: liveRead,
        stationOrgId: matchedStation?.linkedOrgId || null,
        stationOrgName: matchedStation?.name || null,
        mediaOwnerOrgId: matchedOwner?.linkedOrgId || null,
        mediaOwnerOrgName: matchedOwner?.name || null,
        languageOptions: finalLanguageOptions,
        // Preserve linkedTrackId if the caller pre-set it (merge flow restores
        // this from the existing campaign) — else default null so a fresh
        // parse starts clean.
        linkedTrackId: (s as any).linkedTrackId || null,
        deliveryStatus: (s as any).deliveryStatus || 'pending',
        description: (s as any).description || '',
        // Carry the merge-status flag through for the badge UI. UI-only —
        // stripped before persisting to Firestore in handleSave below.
        _mergeStatus: (s as any)._mergeStatus,
        // Retain original AI values when overridden, for the discrepancy badge.
        aiDuration: codeParts.duration ? s.duration : undefined,
        aiLanguage: codeParts.language ? s.language : undefined,
      } as any;
    });
  };

  // Create a blank station entry
  const createBlankStation = (): LinkedStationEntry => ({
    station: '',
    mediaOwner: '',
    language: '',
    duration: '30"',
    flightingCode: '',
    flightingDate: '',
    materialDeadline: '',
    title: '',
    description: '',
    stationOrgId: null,
    stationOrgName: null,
    mediaOwnerOrgId: null,
    mediaOwnerOrgName: null,
    languageOptions: [],
    linkedTrackId: null,
    deliveryStatus: 'pending',
  });

  // Add a blank row to the review table
  const handleAddRow = () => {
    setEditedStations(prev => [...prev, createBlankStation()]);
  };

  /**
   * Bulk-add stations matching the chosen filter values. Skips broadcasters
   * already on the campaign (deduped by stationOrgId or station name).
   *
   * Filter semantics: a broadcaster matches when its `province` is in the
   * selected provinces set (or none selected — matches all) AND its `subType`
   * is in the selected subTypes (or none selected — matches all).
   * Refusing to add anything if both filters are empty (would dump 200 rows).
   */
  const handleApplyBulkFilter = () => {
    if (filterProvinces.length === 0 && filterSubTypes.length === 0) return;

    const matches = stationOptions.filter((s) => {
      const provinceOk = filterProvinces.length === 0 || (s.province && filterProvinces.includes(s.province));
      const subTypeOk = filterSubTypes.length === 0 || (s.subType && filterSubTypes.includes(s.subType));
      return provinceOk && subTypeOk;
    });

    setEditedStations((prev) => {
      // Drop a leading blank row (the default empty row created for manual entry)
      // before appending bulk additions so the campaign doesn't start with a stray
      // empty slot.
      const baseline = prev.length > 0 && !prev[0].station ? prev.slice(1) : prev;

      const seenOrgIds = new Set(baseline.map((s) => s.stationOrgId).filter(Boolean) as string[]);
      const seenNames = new Set(baseline.map((s) => (s.station || '').toLowerCase()).filter(Boolean));

      const additions: LinkedStationEntry[] = matches
        .filter((m) => {
          if (m.linkedOrgId && seenOrgIds.has(m.linkedOrgId)) return false;
          if (seenNames.has(m.name.toLowerCase())) return false;
          return true;
        })
        .map((m) => ({
          // Languages are intentionally left blank — they'll be populated by the
          // flighting code parser once the user enters one (same as the rest of
          // the app). Broadcaster's acceptedLanguages still drives the row-level
          // mismatch indicator and the delivery-time check.
          ...createBlankStation(),
          station: m.name,
          stationOrgId: m.linkedOrgId,
          stationOrgName: m.name,
          mediaOwner: m.parentMediaOwnerName || '',
          mediaOwnerOrgId: m.parentMediaOwnerOrgId || null,
          mediaOwnerOrgName: m.parentMediaOwnerName || null,
        }) as LinkedStationEntry);

      return [...baseline, ...additions];
    });

    // Reset and close the modal
    setFilterProvinces([]);
    setFilterSubTypes([]);
    setShowFilterModal(false);
  };

  /** Live count of broadcasters matching the current filter selections. */
  const bulkMatchCount = (() => {
    if (filterProvinces.length === 0 && filterSubTypes.length === 0) return 0;
    return stationOptions.filter((s) => {
      const provinceOk = filterProvinces.length === 0 || (s.province && filterProvinces.includes(s.province));
      const subTypeOk = filterSubTypes.length === 0 || (s.subType && filterSubTypes.includes(s.subType));
      return provinceOk && subTypeOk;
    }).length;
  })();

  // Start with a blank manual entry
  const handleCreateManually = () => {
    setParsedData({
      header: {
        client: '',
        campaign: '',
        period: '',
        date: '',
        revision: '',
        agencyContacts: [],
        studioOrgId: '',
        studioName: '',
        agencyOrgId: '',
        agencyName: '',
      },
      stations: [],
    });
    setEditedStations([createBlankStation()]);
    setIsManualMode(true);
    setStep('review');
  };

  // Step 1: File selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setError(null);
    setFile(selectedFile);

    try {
      const isCSV = selectedFile.name.toLowerCase().endsWith('.csv');
      const result = isCSV ? await readCSVFile(selectedFile) : await readExcelFile(selectedFile);

      setWorkbook(result.workbook);
      setSheetNames(result.sheetNames);

      if (result.sheetNames.length === 1) {
        setSelectedSheet(result.sheetNames[0]);
        setSelectedSheets([result.sheetNames[0]]);
        handleParse(result.workbook, [result.sheetNames[0]]);
      } else {
        // Default the multi-select to all sheets — matches the common
        // case (chase lists split for producer convenience but all
        // belong to one campaign). User can uncheck any they don't want.
        setSelectedSheets(result.sheetNames);
        setStep('select-sheet');
      }
    } catch (err: any) {
      setError(`Failed to read file: ${err.message}`);
    }
  };

  // Merge flow — user clicks "Merge chase list" in the review step. Snapshots
  // the currently-shown campaign into a ref, then triggers the same file
  // loading logic as a fresh upload. handleParse picks up the ref and
  // switches the AI call into merge mode.
  const handleMergeButtonClick = () => {
    if (!parsedData) return;
    mergeContextRef.current = {
      header: parsedData.header,
      stations: editedStations.map((s) => ({
        station: s.station,
        mediaOwner: s.mediaOwner,
        language: s.language,
        duration: s.duration,
        flightingCode: s.flightingCode,
        flightingDate: s.flightingDate,
        materialDeadline: s.materialDeadline,
        title: s.title,
        description: (s as any).description || '',
        stationOrgId: s.stationOrgId,
        mediaOwnerOrgId: s.mediaOwnerOrgId,
        // linkedTrackId is intentionally NOT sent — the AI shouldn't see
        // internal track ids. We restore it client-side by flighting-code
        // match after the merge returns.
        linkedTrackId: (s as any).linkedTrackId || null,
        deliveryStatus: (s as any).deliveryStatus || 'pending',
      })) as any,
    };
    mergeFileInputRef.current?.click();
  };

  const handleMergeFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Shares the exact same body as handleFileSelect — the merge context
    // ref set by handleMergeButtonClick above tells handleParse what to do.
    await handleFileSelect(e);
  };

  // Step 2: Parse selected sheet(s). Accepts either a single sheet name
  // (legacy call sites) or an array (multi-sheet flow). Internally
  // resolves to an array + display label so downstream code has one
  // uniform representation.
  const handleParse = async (wb?: XLSX.WorkBook, sheets?: string | string[]) => {
    const useWorkbook = wb || workbook;
    const requested: string[] = Array.isArray(sheets)
      ? sheets
      : sheets
      ? [sheets]
      : (selectedSheets.length > 0 ? selectedSheets : (selectedSheet ? [selectedSheet] : []));
    if (!useWorkbook || requested.length === 0) return;

    // Display label follows the sheet count: one → the sheet name;
    // many → a comma-joined list surfaced in the review header. Kept in
    // sync with selectedSheet(s) state so the header + retry paths
    // agree with whatever the parser actually saw.
    const displayLabel = requested.length === 1 ? requested[0] : requested.join(', ');
    setSelectedSheet(displayLabel);
    setSelectedSheets(requested);

    setStep('parsing');
    setError(null);
    setHiddenRowWarning(null);

    try {
      const extraction = extractMultipleSheets(useWorkbook, requested);

      // Warn about hidden rows. Wording adapts to single vs multi-sheet
      // so the count in the message accurately reflects the summed
      // total across every selected sheet.
      if (extraction.hiddenRowCount > 0) {
        const sheetLabel = requested.length === 1
          ? 'This sheet has'
          : `The ${requested.length} selected sheets contain`;
        setHiddenRowWarning(
          `${sheetLabel} ${extraction.hiddenRowCount} hidden row${extraction.hiddenRowCount > 1 ? 's' : ''} that ${extraction.hiddenRowCount > 1 ? 'were' : 'was'} excluded from parsing. ` +
          `Showing ${extraction.visibleRows} of ${extraction.totalRows} total rows.`
        );
      }

      // Pre-build canonical org lookups so Gemini can map chase-list names
      // (e.g. "GOOD HOPE") to canonical org names (e.g. "Good Hope FM") and
      // return the matching orgId directly. We only include entries that
      // have a linkedOrgId — unlinked entries can't be routed to in-app
      // anyway, so matching them adds no value.
      const broadcasterLookups = stationOptions
        .filter((s) => s.linkedOrgId)
        .map((s) => ({ id: s.linkedOrgId as string, name: s.name }));
      const mediaOwnerLookups = mediaOwnerOptions
        .filter((m) => m.linkedOrgId)
        .map((m) => ({ id: m.linkedOrgId as string, name: m.name }));
      // Studio + agency options are already filtered to entries with a
      // linkedOrgId and shaped as { id, name } so they can be passed
      // straight through.
      // Pick up merge context (populated by the "Merge chase list" button).
      // Cleared unconditionally after this call so a subsequent Re-parse
      // doesn't accidentally inherit merge mode.
      const existingCampaign = mergeContextRef.current;
      const isMerge = !!existingCampaign;
      mergeContextRef.current = null;

      // Clients aren't cached in useCollectionCache today (fetched via a
      // per-mount /client read into clientOptions). Reshape to the same
      // {id, name} lookup the prompt uses for studios / agencies so the
      // AI can match extracted client text against known clients and
      // avoid misclassifying product names ("Essential Combo & Subsidy")
      // as client names.
      const clientLookups = clientOptions.map((c) => ({ id: c.value, name: c.label }));
      const result = await parseChaseListWithAI(
        extraction.data,
        displayLabel,
        broadcasterLookups,
        mediaOwnerLookups,
        studioOptions,
        agencyOptions,
        clientLookups,
        existingCampaign,
      );
      // Ensure header.client is never null (MUI Autocomplete can't handle null values)
      if (result.header && result.header.client == null) {
        result.header.client = '';
      }
      // Defensive defaults for the new studio + agency header fields. Older
      // AI responses (or AI runs against an early-cache empty lookup) may
      // omit them entirely; we normalise here so downstream code doesn't
      // have to keep doing `?? ''` everywhere.
      if (result.header) {
        if (result.header.studioOrgId == null) result.header.studioOrgId = '';
        if (result.header.studioName == null) result.header.studioName = '';
        if (result.header.agencyOrgId == null) result.header.agencyOrgId = '';
        if (result.header.agencyName == null) result.header.agencyName = '';

        // Auto-link safety net — when the AI extracted a name that
        // exactly matches an entry in our studio/agency options but
        // failed to resolve the orgId, snap the link on client-side.
        // Mirrors the on-type auto-link in the modal's Autocomplete,
        // which doesn't fire for AI-populated values because they
        // arrive via state, not via a user input event.
        if (result.header.studioName && !result.header.studioOrgId) {
          const s = result.header.studioName.trim().toLowerCase();
          const hit = studioOptions.find((o) => (o.name || '').trim().toLowerCase() === s);
          if (hit) {
            result.header.studioOrgId = hit.id;
            result.header.studioName = hit.name;
          }
        }
        if (result.header.agencyName && !result.header.agencyOrgId) {
          const a = result.header.agencyName.trim().toLowerCase();
          const hit = agencyOptions.find((o) => (o.name || '').trim().toLowerCase() === a);
          if (hit) {
            result.header.agencyOrgId = hit.id;
            result.header.agencyName = hit.name;
          }
        }
        // Collapse the period into a canonical `DD MMM YYYY - DD MMM YYYY`
        // range. Chase lists often list weekly flighting segments
        // (e.g. "08 - 13 June, 15 - 20 June, 22 - 27 June, …") which is
        // noisy to read. normaliseCampaignPeriod extracts every date-
        // like token, finds the min and max, and returns the summary.
        // If it can't parse anything it returns the input verbatim.
        if (result.header.period) {
          result.header.period = normaliseCampaignPeriod(result.header.period);
        }
      }

      // In merge mode, the AI is instructed to leave the header alone. Even
      // so, defensively snap the header back to the existing values in case
      // the model returned something different — the merge rules only apply
      // to stations.
      if (isMerge && existingCampaign) {
        result.header = { ...(existingCampaign.header as any), agencyContacts: (result.header as any).agencyContacts || [] };
      }

      // Drop "Media Host" rows — placeholder / legend rows the AI
      // sometimes emits as a station. Client-side safety net to the
      // prompt rule (belt-and-braces because the AI slips occasionally).
      // Matches any casing / separator — "Media Host", "media-host",
      // "MEDIAHOST", "Media_Host" all filtered.
      const isMediaHostSentinel = (v: any) => {
        const s = String(v || '').trim().toLowerCase().replace(/[\s_\-.]+/g, '');
        return s === 'mediahost';
      };
      const preMediaHostCount = result.stations.length;
      result.stations = result.stations.filter(
        (s) => !isMediaHostSentinel(s.station) && !isMediaHostSentinel(s.mediaOwner),
      );
      const droppedMediaHostCount = preMediaHostCount - result.stations.length;
      if (droppedMediaHostCount > 0) {
        console.log(`[ChaseListUploader] Dropped ${droppedMediaHostCount} "Media Host" placeholder row(s)`);
      }

      // Normalise flighting codes: dashes / slashes / en-em dashes / internal
      // spaces → underscores. Underscore is the canonical separator that the
      // track-name matcher relies on; normalising at the parse boundary means
      // a chase list using `SHMA-30-248-E` matches an upload using `SHMA_30_248_E`.
      const { values: normalisedCodes, changedCount } = normaliseFlightingCodeBatch(
        result.stations.map((s) => s.flightingCode),
      );
      result.stations = result.stations.map((s, i) => ({ ...s, flightingCode: normalisedCodes[i] }));
      if (changedCount > 0) {
        showSuccess(`Normalised ${changedCount} flighting code${changedCount === 1 ? '' : 's'} (dashes → underscores)`);
      }

      // MERGE POST-PROCESSING. Runs only when merge mode was active:
      //   1. Compute per-row status (new / updated / unchanged) by
      //      comparing to the existing stations, matched on flighting
      //      code.
      //   2. Restore linkedTrackId + deliveryStatus on matched rows so
      //      merges don't disrupt existing links.
      //   3. Detect duplicate flighting codes in the merged output and
      //      warn the user.
      if (isMerge && existingCampaign) {
        const norm = (v: any) => (v == null ? '' : String(v)).trim().toLowerCase();
        const codeKey = (c: string | undefined | null) => {
          const n = normaliseFlightingCode(c || '');
          return n && n !== 'TBA' ? n.toLowerCase() : '';
        };
        const oldByCode = new Map<string, any>();
        for (const s of existingCampaign.stations) {
          const key = codeKey(s.flightingCode);
          if (key) oldByCode.set(key, s);
        }
        const TRACKED_FIELDS = [
          'station', 'mediaOwner', 'language', 'duration', 'materialDeadline',
          'flightingDate', 'title', 'description',
        ];
        // UPDATE-ONLY MERGE (2026-07-10): the AI is instructed to emit
        // only existing codes with their fields updated from the new
        // chase list; new codes on the new list are dropped. As a safety
        // net we also filter here — if the AI slips a new code through
        // we count it as "ignored" and remove it from the output so the
        // review table only shows the update-only merged campaign.
        let updatedCount = 0;
        let ignoredCount = 0;
        const filteredStations: any[] = [];
        for (const station of result.stations) {
          const key = codeKey(station.flightingCode);
          const old = key ? oldByCode.get(key) : null;
          if (!old) {
            // AI slipped a non-existing code through — drop it. Do not
            // emit into filteredStations. Count for the user-facing
            // toast so they know something was skipped.
            ignoredCount++;
            continue;
          }
          if (old.linkedTrackId) (station as any).linkedTrackId = old.linkedTrackId;
          if (old.deliveryStatus) (station as any).deliveryStatus = old.deliveryStatus;
          const hasChanges = TRACKED_FIELDS.some((f) => norm((old as any)[f]) !== norm((station as any)[f]));
          (station as any)._mergeStatus = hasChanges ? 'updated' : 'unchanged';
          if (hasChanges) updatedCount++;
          filteredStations.push(station);
        }
        result.stations = filteredStations;
        // Duplicate detection: same code + same station. Two rows with
        // the same code on different stations is legitimate (one spot,
        // many broadcasters). Composite key mirrors the fresh-parse
        // branch above.
        const mergeStationKey = (s: any): string => {
          const orgId = String((s as any).stationOrgId || '').trim();
          if (orgId) return `org:${orgId}`;
          return `name:${String(s.station || '').trim().toLowerCase()}`;
        };
        const seen = new Map<string, { code: string; station: string; count: number }>();
        for (const s of result.stations) {
          const codeNorm = codeKey(s.flightingCode);
          if (!codeNorm) continue;
          const key = `${codeNorm}|${mergeStationKey(s)}`;
          const existing = seen.get(key);
          if (existing) {
            existing.count++;
          } else {
            seen.set(key, { code: codeNorm, station: String(s.station || '').trim(), count: 1 });
          }
        }
        const dupes = Array.from(seen.values()).filter((d) => d.count > 1);
        if (dupes.length > 0) {
          const dupeList = dupes.map((d) => (d.station ? `${d.code} on ${d.station}` : d.code)).join(', ');
          setError(`Warning: duplicate flighting code${dupes.length > 1 ? 's' : ''} after merge — please review: ${dupeList}`);
        }
        const ignoredSuffix = ignoredCount > 0 ? ` · ${ignoredCount} ignored (not on original campaign)` : '';
        showSuccess(`Merged: ${updatedCount} updated${ignoredSuffix}`);
      } else {
        // Fresh (non-merge) parse — surface duplicate flighting codes
        // early so the user can resolve them before hitting Save.
        // Common with multi-sheet input where the same spot appears in
        // multiple sheets. Save gate re-checks and hard-blocks; this
        // banner is the friendly heads-up.
        //
        // Duplicate = SAME flighting code AND SAME station. The same
        // spot going to multiple stations is legitimate (one spot,
        // many broadcasters) — those rows share a code but have
        // different stations, so they don't count. The composite key
        // uses stationOrgId when available (canonical) and falls back
        // to the normalised station name for unlinked rows.
        //
        // Only real codes count. Placeholders like "ALL_SPOT_CODES",
        // "SEE_LIST", "SAME_AS_ABOVE" that the AI occasionally emits
        // when the source chase list has batch-level notes must not
        // trigger the duplicate warning. Real SA flighting codes
        // always carry a numeric duration segment — the /\d/ check is
        // the cheap, high-confidence signal.
        const stationKey = (s: any): string => {
          const orgId = String((s as any).stationOrgId || '').trim();
          if (orgId) return `org:${orgId}`;
          return `name:${String(s.station || '').trim().toLowerCase()}`;
        };
        const dupeCounts = new Map<string, { code: string; station: string; count: number }>();
        for (const s of result.stations) {
          const n = normaliseFlightingCode(s.flightingCode || '');
          if (!n || n === 'TBA' || n === 'TBC') continue;
          if (!/\d/.test(n)) continue;
          const key = `${n}|${stationKey(s)}`;
          const existing = dupeCounts.get(key);
          if (existing) {
            existing.count++;
          } else {
            dupeCounts.set(key, { code: n, station: String(s.station || '').trim(), count: 1 });
          }
        }
        const dupes = Array.from(dupeCounts.values()).filter((d) => d.count > 1);
        if (dupes.length > 0) {
          const dupeList = dupes.map((d) => (d.station ? `${d.code} on ${d.station}` : d.code)).join(', ');
          setError(
            `Duplicate flighting code${dupes.length > 1 ? 's' : ''} found across the selected sheets: ${dupeList}. Delete or edit the duplicate row${dupes.length > 1 ? 's' : ''} before saving. (Same code on different stations is fine — this only flags same code + same station.)`,
          );
        }
      }

      setParsedData(result);
      setEditedStations(toLinkedStations(result.stations));
      setIsManualMode(false);
      setStep('review');
    } catch (err: any) {
      setError(`Parsing failed: ${err.message}`);
      setStep(sheetNames.length > 1 ? 'select-sheet' : 'upload');
    }
  };

  // Edit handlers
  const handleEditField = (index: number, field: string, value: any) => {
    setEditedStations(prev => {
      const updated = [...prev];
      const applyToRow = (row: LinkedStationEntry, fieldValue: any): LinkedStationEntry => {
        // Normalise flighting code separators on the fly so the user sees the
        // canonical form as they type or paste. `partial: true` keeps the
        // trailing `_` so typing a separator at the end of the field isn't
        // silently erased mid-edit (the batch save path strips it later).
        const normalisedValue = field === 'flightingCode'
          ? normaliseFlightingCode(fieldValue, { partial: true })
          : fieldValue;
        const out = { ...row, [field]: normalisedValue };
        // When the flighting code changes, derive duration/language from it.
        // The code is the source of truth — if it parses, override the fields;
        // if it no longer parses (e.g. user cleared the code), unlock by leaving
        // existing values in place but clearing the AI shadows.
        if (field === 'flightingCode') {
          const codeParts = parseFlightingCode(normalisedValue);
          if (codeParts.duration) out.duration = codeParts.duration;
          if (codeParts.language) {
            out.languageOptions = [codeParts.language];
            out.language = codeParts.language.label;
          }
        }
        return out;
      };

      updated[index] = applyToRow(updated[index], value);

      // Sticky sync: while "apply to all" is on for this field AND the user is
      // editing row 0, mirror the same value down to every other row.
      if (
        index === 0 &&
        (APPLY_TO_ALL_FIELDS as readonly string[]).includes(field) &&
        applyToAll[field as ApplyToAllField]
      ) {
        for (let i = 1; i < updated.length; i++) {
          updated[i] = applyToRow(updated[i], value);
        }
      }

      return updated;
    });
  };

  /** Toggle "apply to all rows" for a specific field (flightingCode / deadline). */
  const handleToggleApplyToAll = (field: ApplyToAllField, checked: boolean) => {
    setApplyToAll((prev) => ({ ...prev, [field]: checked }));
    if (!checked) return;
    // On enable, immediately propagate row 0's current value to every other row.
    setEditedStations((prev) => {
      if (prev.length === 0) return prev;
      const value = (prev[0] as any)[field] || '';
      const codeParts = field === 'flightingCode' ? parseFlightingCode(value) : null;
      return prev.map((row, i) => {
        if (i === 0) return row;
        const next: LinkedStationEntry = { ...row, [field]: value };
        if (codeParts?.duration) next.duration = codeParts.duration;
        if (codeParts?.language) {
          next.languageOptions = [codeParts.language];
          next.language = codeParts.language.label;
        }
        return next;
      });
    });
  };

  /**
   * "Apply to all" toggle rendered in the column header for the two
   * sticky-syncable fields (Flighting Code / Deadline).
   * Returns a flex-rendered label containing a small checkbox visual + text.
   * The native input is visually hidden but still receives clicks via the
   * surrounding <label>, so accessibility + keyboard activation still work.
   *
   * Pair this with a flex header (justify-content: space-between) so the title
   * sits left and the toggle right-justified on the same horizontal line.
   */
  const renderApplyToAllHeaderToggle = (field: ApplyToAllField) => {
    if (editedStations.length <= 1) return null;
    const checked = applyToAll[field];
    return (
      <label
        title="Apply row 1's value to every row"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          margin: 0,
          fontSize: '0.7rem',
          fontWeight: 400,
          color: checked ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.55)',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => handleToggleApplyToAll(field, e.target.checked)}
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
        />
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: '14px',
            height: '14px',
            border: `1px solid ${checked ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.5)'}`,
            borderRadius: '3px',
            backgroundColor: 'transparent',
            position: 'relative',
            flexShrink: 0,
            transition: 'border-color 120ms ease',
          }}
        >
          {checked && (
            <span
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                lineHeight: 1,
                color: '#fff',
              }}
            >
              ✓
            </span>
          )}
        </span>
        apply to all
      </label>
    );
  };

  const handleStationOrgSelect = (index: number, entry: GlobalEntry | null) => {
    setEditedStations(prev => {
      const updated = [...prev];
      const current = updated[index];
      const next: LinkedStationEntry = {
        ...current,
        stationOrgId: entry?.linkedOrgId || null,
        stationOrgName: entry?.name || null,
        station: entry?.name || current.station,
      };

      const parentOrgId = entry?.parentMediaOwnerOrgId || null;
      const parentName = entry?.parentMediaOwnerName || null;

      if (parentOrgId && parentName) {
        if (!current.mediaOwnerOrgId) {
          // Media owner was empty — silently auto-fill.
          next.mediaOwnerOrgId = parentOrgId;
          next.mediaOwnerOrgName = parentName;
          next.mediaOwner = parentName;
        } else if (current.mediaOwnerOrgId !== parentOrgId) {
          // Conflict — leave media owner as-is and surface a banner so the user
          // can decide. The banner persists in mediaOwnerMismatch until they
          // accept ("Yes, update") or dismiss ("Cancel").
          setMediaOwnerMismatch(prevMM => ({
            ...prevMM,
            [index]: { parentOrgId, parentName },
          }));
        } else {
          // Picked broadcaster matches the existing media owner — clear any
          // stale banner from a previous pick.
          setMediaOwnerMismatch(prevMM => ({ ...prevMM, [index]: undefined }));
        }
      } else {
        // Broadcaster has no parent → leave media owner alone; clear any banner.
        setMediaOwnerMismatch(prevMM => ({ ...prevMM, [index]: undefined }));
      }

      updated[index] = next;
      return updated;
    });
  };

  const handleMediaOwnerOrgSelect = (index: number, entry: GlobalEntry | null) => {
    setEditedStations(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        mediaOwnerOrgId: entry?.linkedOrgId || null,
        mediaOwnerOrgName: entry?.name || null,
        mediaOwner: entry?.name || updated[index].mediaOwner,
      };
      return updated;
    });
    // Changing media owner manually invalidates any active mismatch banner.
    setMediaOwnerMismatch(prevMM => ({ ...prevMM, [index]: undefined }));
  };

  /** Accept the mismatch suggestion — set media owner to the broadcaster's parent. */
  const handleAcceptMismatch = (index: number) => {
    const mm = mediaOwnerMismatch[index];
    if (!mm) return;
    setEditedStations(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        mediaOwnerOrgId: mm.parentOrgId,
        mediaOwnerOrgName: mm.parentName,
        mediaOwner: mm.parentName,
      };
      return updated;
    });
    setMediaOwnerMismatch(prevMM => ({ ...prevMM, [index]: undefined }));
  };

  const handleDismissMismatch = (index: number) => {
    setMediaOwnerMismatch(prevMM => ({ ...prevMM, [index]: undefined }));
  };

  const handleLanguageChange = (index: number, value: { label: string; value: string }[]) => {
    setEditedStations(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        languageOptions: value,
        language: value.map(v => v.label).join(', '),
      };
      return updated;
    });
  };

  const handleRemoveStation = (index: number) => {
    setEditedStations(prev => prev.filter((_, i) => i !== index));
  };

  // Resolve client to a {label, value} object, matching case-insensitively against existing clients
  const resolveClient = async (
    clientInput: string | { label: string; value: string },
    firestoreCollection: any,
    addDoc: any,
  ): Promise<{ label: string; value: string }> => {
    // Already a selected option from autocomplete
    if (typeof clientInput === 'object' && clientInput.label && clientInput.value) {
      return clientInput;
    }

    const clientName = String(clientInput || '').trim();
    if (!clientName) return { label: '', value: '' };

    // Case-insensitive match against loaded client options
    const match = clientOptions.find(
      c => c.label.toLowerCase() === clientName.toLowerCase()
    );
    if (match) return match;

    // No match — create new client in global /client collection
    const newClientDoc = await addDoc(firestoreCollection(firebase.db, 'client'), {
      label: clientName,
      value: clientName.toLowerCase().replace(/\s+/g, '_'),
      name: clientName,
      createdAt: Date.now(),
    });

    const newClient = { label: clientName, value: newClientDoc.id };
    // Update local options so repeated saves don't create duplicates
    setClientOptions(prev => [...prev, newClient].sort((a, b) => a.label.localeCompare(b.label)));
    return newClient;
  };

  // Save to Firestore
  const handleSave = async () => {
    if (!currentOrgId || !firebase || !parsedData) return;

    // Duplicate flighting code gate. Multi-sheet parsing can produce two
    // stations with the same code (e.g. same code listed in "Community
    // Radio" AND "Metropolitan" sheets by mistake). We surface a
    // warning banner at parse time, but the save flow is where we
    // actually enforce — a campaign with duplicate code+station pairs
    // breaks the auto-match / delivery pipeline downstream. The user
    // must resolve duplicates (delete rows) before we let them save.
    //
    // Duplicate = SAME flighting code AND SAME station. The same spot
    // going to multiple stations is legitimate (one spot, many
    // broadcasters). Composite key uses stationOrgId when linked,
    // falls back to normalised station name otherwise.
    //
    // Skip rules mirror the parse-time detector:
    //  - Empty codes and "TBA"/"TBC" are pending stations and multiple
    //    can legitimately co-exist.
    //  - Placeholders without any digit (e.g. "ALL_SPOT_CODES") aren't
    //    real codes — AI slippage — so we don't block save on them.
    //    They'll still land in Firestore looking wrong, but the user's
    //    manual review is expected to catch that.
    {
      const stationKey = (s: any): string => {
        const orgId = String((s as any).stationOrgId || '').trim();
        if (orgId) return `org:${orgId}`;
        return `name:${String(s.station || '').trim().toLowerCase()}`;
      };
      const dupeCounts = new Map<string, { code: string; station: string; count: number }>();
      for (const s of editedStations) {
        const norm = normaliseFlightingCode(s.flightingCode || '');
        if (!norm || norm === 'TBA' || norm === 'TBC') continue;
        if (!/\d/.test(norm)) continue;
        const key = `${norm}|${stationKey(s)}`;
        const existing = dupeCounts.get(key);
        if (existing) {
          existing.count++;
        } else {
          dupeCounts.set(key, { code: norm, station: String(s.station || '').trim(), count: 1 });
        }
      }
      const dupes = Array.from(dupeCounts.values()).filter((d) => d.count > 1);
      if (dupes.length > 0) {
        const dupeList = dupes.map((d) => (d.station ? `${d.code} on ${d.station}` : d.code)).join(', ');
        setError(
          `Cannot save — duplicate flighting code${dupes.length > 1 ? 's' : ''} on the same station: ${dupeList}. Please remove or edit the duplicate row${dupes.length > 1 ? 's' : ''} before saving.`,
        );
        return;
      }
    }

    // Unlinked-orgs warning (create + edit). A station whose broadcaster
    // or media-house NAME is filled in but has no linked in-app org
    // (stationOrgId / mediaOwnerOrgId absent) can only receive an
    // EXTERNAL delivery link — no in-app delivery or library copy will
    // happen. This is the exact oversight that caused delivered campaigns
    // to never reach a media house's library. Empty name fields are
    // ignored (a station legitimately may have no media owner). Proceed
    // lets the user ship anyway (external-only is a valid path); Go back
    // aborts so they can link the org first.
    {
      const unlinkedRows = editedStations
        .map((s) => {
          const stationName = String((s as any).station || '').trim();
          const mediaOwnerName = String((s as any).mediaOwner || '').trim();
          const missingStation = !!stationName && !(s as any).stationOrgId;
          const missingMediaOwner = !!mediaOwnerName && !(s as any).mediaOwnerOrgId;
          return { station: stationName, mediaOwner: mediaOwnerName, missingStation, missingMediaOwner };
        })
        .filter((r) => r.missingStation || r.missingMediaOwner);
      if (unlinkedRows.length > 0) {
        const proceed = await confirmUnlinkedOrgs(unlinkedRows);
        if (!proceed) {
          // Go back — abort before setSaving so the button stays live.
          return;
        }
      }
    }

    // Agency reminder — only for NEW campaigns. Warns whenever the
    // agency field is blank (regardless of studio). Skipping studio
    // alone is fine — many campaigns run without a tagged studio —
    // but a missing agency is the common oversight worth flagging.
    // Skip proceeds with blank agency on this save; Add aborts so the
    // user can fill in.
    if (!editJob) {
      const noAgency = !(parsedData.header as any).agencyName && !(parsedData.header as any).agencyOrgId;
      if (noAgency) {
        const skip = await confirmMissingStudioAgency();
        if (!skip) {
          // User chose Add — abort the save; they'll fill in and click
          // Save again. Don't setSaving anywhere in this branch so the
          // button doesn't get stuck.
          return;
        }
      }
    }

    setSaving(true);
    setSaveStage({ label: 'Saving campaign…', pct: 20 });
    setError(null);

    try {
      const { collection: firestoreCollection, addDoc, doc: firestoreDoc, updateDoc, getDoc: firestoreGetDoc } = await import('firebase/firestore');
      const { getOrgCollectionPath } = await import('../../constants/paths');
      const { getAuth } = await import('firebase/auth');

      const auth = getAuth();
      const user = auth.currentUser;

      // Resolve client name to existing or new {label, value} entry
      const resolvedClient = await resolveClient(
        parsedData.header.client as any,
        firestoreCollection,
        addDoc,
      );

      const header = {
        ...parsedData.header,
        client: resolvedClient,
      };

      // DISABLED [2026-06-23] — auto-creating /broadcaster and /mediaowner
      // entries on save was silently flooding the global collections with
      // duplicates (every chase-list variant name like "GOOD HOPE" became
      // a new unlinked entry alongside the canonical "Good Hope FM"). With
      // AI-driven org matching now in place (see parseChaseListWithAI org
      // lookups), canonical names should land in the correct linked entry.
      // For genuinely new orgs, admins should add them via Global Org
      // Manager — never via a silent side-effect of saving a campaign.
      // To revive, restore the loop that addDoc-s to /broadcaster and
      // /mediaowner here.

      // Unlink all tracks when editing (stations may have changed).
      // Final pass through normaliseFlightingCode WITHOUT partial mode
      // strips any trailing `_` the user left dangling mid-edit, so the
      // persisted form stays canonical.
      // Persist stations. Preserve any existing linkedTrackId + deliveryStatus
      // (the merge flow relies on this to survive save; general edits also
      // shouldn't wipe existing track links). Strip the UI-only _mergeStatus
      // flag so it doesn't leak into Firestore.
      const stationsWithClearedLinks = editedStations.map((s) => {
        const { _mergeStatus, ...rest } = s as any;
        void _mergeStatus;
        // Recompute isLiveRead from the FINAL normalised code so manual
        // edits (e.g. user retypes the code to add / remove `_LR`) are
        // honoured. isLiveReadCode short-circuits on empty / TBA so
        // clearing the code also clears the flag.
        const normalisedCode = normaliseFlightingCode(rest.flightingCode);
        // Backfill a stable per-row id if this station doesn't have
        // one yet. Legacy jobs pre-dating the id introduction will
        // get their ids populated on the first save that touches the
        // stations array. The id survives every future save + sync
        // and is the identity used by the delivery pipeline for
        // matching rows across source, campaign copies, delivery
        // records and broadcaster copies. `crypto.randomUUID()` gives
        // us a 36-char UUID with no external dep.
        const id: string =
          rest.id && typeof rest.id === 'string' && rest.id.length > 0
            ? rest.id
            : (typeof crypto !== 'undefined' && (crypto as any).randomUUID
                ? (crypto as any).randomUUID()
                : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
        return {
          ...rest,
          id,
          flightingCode: normalisedCode,
          isLiveRead: isLiveReadCode(normalisedCode),
          linkedTrackId: rest.linkedTrackId ?? null,
          deliveryStatus: rest.deliveryStatus || 'pending',
        };
      });

      // Actor snapshot for the history log.
      const actor = {
        actorUid: user?.uid || '',
        actorName: user?.displayName || user?.email || '',
        actorOrgId: currentOrgId,
        actorOrgName: (useOrganizationStore.getState() as any).currentOrgName || '',
      };

      if (editJob) {
        // Redirect writes to the source when editing a copy. The actor's
        // org (currentOrgId) is still recorded in the history entry so
        // the audit trail shows who initiated the change.
        const isCopy = !!editJob.isCampaignCopy;
        const targetOrgId = isCopy ? (editJob.sourceOrgId || currentOrgId) : currentOrgId;
        const targetJobId = isCopy ? (editJob.sourceJobId || editJob.id) : editJob.id;
        const actorRole: 'source' | 'studio' | 'agency' | 'creator' =
          isCopy ? (editJob.copyRole as any) || 'creator' : 'source';

        // Capture BEFORE state for the diff — we use what came in via
        // editJob (loaded from Firestore at modal-open) rather than
        // re-reading, since the diff is against what the user saw.
        const beforeSnapshot = {
          ...(editJob.header || {}),
          stations: editJob.stations || [],
        };
        const afterSnapshot = {
          ...header,
          stations: stationsWithClearedLinks,
        };

        // PHASE 2 — detect deletion of already-delivered stations.
        // Match "before" vs "after" by the synthetic station id
        // introduced in Phase 1. A station qualifies for cascade
        // delete when it WAS present in the loaded edit state, WAS
        // flagged 'delivered', and is NOT in the new stations array.
        // Legacy stations without ids can't be identified for
        // cascade — they get treated as untouched (safe default).
        const beforeStations = editJob.stations || [];
        const afterIds = new Set(
          stationsWithClearedLinks.map((s: any) => s.id).filter(Boolean),
        );
        // ALL deleted stations — used to drive the cascade cleanup
        // (broadcaster copies for delivered rows + audiometa cleanup
        // for both delivered AND non-delivered rows).
        const deletedStations = beforeStations.filter((s: any) =>
          s.id && !afterIds.has(s.id),
        );
        // DELIVERED subset — used only for the confirmation modal so
        // the user is warned before removing content that broadcasters
        // have already received.
        const deletedDeliveredStations = deletedStations.filter((s: any) =>
          s.deliveryStatus === 'delivered',
        );

        if (deletedDeliveredStations.length > 0) {
          const ok = await confirmDeletedDelivered(deletedDeliveredStations);
          if (!ok) {
            // User cancelled — abort and RESET the save state so the
            // button becomes clickable again. Without this the spinner
            // stays spinning forever and the modal is stuck.
            setSaving(false);
            setSaveStage(null);
            return;
          }
        }

        // Option A — re-deliver corrected stations. Detect already-
        // `delivered` stations whose broadcaster / media-house org LINK
        // changed in this edit (e.g. an unlinked recipient was linked to
        // the right org, or re-pointed to a different one). Their existing
        // delivery is now stale/incorrect, so we (1) withdraw it via
        // cascadeDeleteStations (below, after the main writes) and (2)
        // reset the station to `pending` so the normal partial-redeliver
        // Deliver CTA re-ships it to the corrected recipient. Matched by
        // the synthetic station id against the loaded edit state;
        // never-delivered or id-less rows are ignored (safe default).
        const redeliverStations = beforeStations.filter((b: any) => {
          if ((b as any).deliveryStatus !== 'delivered' || !b.id) return false;
          const after = stationsWithClearedLinks.find((a: any) => a.id === b.id);
          if (!after) return false; // removed in this edit — cascadeDeleteStations handles it
          const stationOrgChanged = ((b as any).stationOrgId || null) !== ((after as any).stationOrgId || null);
          const mediaOwnerOrgChanged = ((b as any).mediaOwnerOrgId || null) !== ((after as any).mediaOwnerOrgId || null);
          return stationOrgChanged || mediaOwnerOrgChanged;
        });
        const redeliverIds = new Set<string>(
          redeliverStations.map((s: any) => s.id).filter(Boolean),
        );
        if (redeliverStations.length > 0) {
          const ok = await confirmRedeliverCorrected(redeliverStations);
          if (!ok) {
            setSaving(false);
            setSaveStage(null);
            return;
          }
        }

        // Compute the diff up front so we can (a) decide whether this
        // edit should bounce the campaign back to 'draft' and (b) reuse
        // the change list for the history log below.
        //
        // Status rule: any station ADD or station FIELD change means
        // there is new / modified content that needs re-linking or re-
        // delivery — so we bounce back to 'draft' (the auto-match
        // pipeline / next delivery flow will move it forward again).
        // A pure DELETE-only edit leaves the surviving stations
        // untouched — if the campaign was already 'delivered' those
        // spots are still delivered, so we preserve the existing
        // status. Header edits alone (e.g. period rename) also
        // preserve status — they don't invalidate any delivered spot.
        const changes = computeCampaignDiff(beforeSnapshot, afterSnapshot);
        const hasAddOrUpdate = changes.some(
          (c) => c.kind === 'station_added' || c.kind === 'station_field',
        );
        // Field-edit signal for the Publish-Updates CTA. Stations being
        // RE-DELIVERED (Option A) are excluded: linking a broadcaster
        // changes its display name — a tracked field — but that corrected
        // value is already carried by the re-delivery itself, so it must
        // NOT also trip "Publish Updates" (which would surface a spurious
        // CTA after the re-deliver completes). Field edits on OTHER
        // delivered stations still count (genuine Publish-Updates case).
        let hasFieldEditsOnly = changes.some((c) => c.kind === 'station_field');
        if (hasFieldEditsOnly && redeliverIds.size > 0) {
          const nonRedeliverChanges = computeCampaignDiff(
            { ...beforeSnapshot, stations: beforeStations.filter((s: any) => !(s.id && redeliverIds.has(s.id))) },
            { ...afterSnapshot, stations: stationsWithClearedLinks.filter((s: any) => !(s.id && redeliverIds.has(s.id))) },
          );
          hasFieldEditsOnly = nonRedeliverChanges.some((c) => c.kind === 'station_field');
        }
        const wasDelivered = editJob.status === 'delivered';
        // Status rule (2026-08-07 revision — snapshot + publish-updates
        // model):
        //   - Delivered campaign + ANY change → status STAYS 'delivered'
        //     so the UI keeps showing "Delivered" (the snapshot itself
        //     hasn't changed). Station-field edits set
        //     `hasPendingUpdates: true` so the CTA switches to "Publish
        //     Updates". Station adds land as `deliveryStatus: 'pending'`
        //     and the CTA becomes "Deliver Pending". Deletes are handled
        //     by the cascade in the flow below.
        //   - Draft / never-delivered campaign → same rule as before:
        //     any edit keeps it at draft.
        const nextStatus = wasDelivered ? 'delivered' : 'draft';
        // Set the pending-updates flag when field edits happened on a
        // delivered campaign. Adds don't set this — new stations show up
        // via the existing pending-station flow. Deletes don't set it
        // either — they cascade silently.
        const nextHasPendingUpdates = wasDelivered && hasFieldEditsOnly
          ? true
          : ((editJob as any).hasPendingUpdates || false);

        // Reset re-deliver-flagged stations (Option A) back to `pending`
        // so the Deliver CTA re-ships them. Every other station keeps its
        // existing delivered/pending status untouched — only the corrected
        // rows move. No-op clone when nothing was flagged.
        const finalStations = redeliverIds.size > 0
          ? stationsWithClearedLinks.map((s: any) =>
              (s.id && redeliverIds.has(s.id)) ? { ...s, deliveryStatus: 'pending' } : s)
          : stationsWithClearedLinks;

        const jobRef = firestoreDoc(firebase.db, getOrgCollectionPath(targetOrgId, 'jobs'), targetJobId);
        const updatedPayload = {
          ...header,
          stations: finalStations,
          stationCount: finalStations.length,
          linkedCount: 0,
          status: nextStatus,
          hasPendingUpdates: nextHasPendingUpdates,
          lastEditedAt: Date.now(),
          lastEditedBy: user?.uid || '',
          lastEditedByName: user?.displayName || user?.email || '',
        };
        // Capture the source doc's PRE-UPDATE studio/agency tags before
        // the updateDoc call overwrites them. Without this, when the
        // user removes an agency, syncCampaignCopies would read the
        // POST-update tags (empty) as "old" and skip orphan cleanup —
        // the detag would silently fail to remove the copy from the
        // agency's org. Best-effort: on a brand-new doc that doesn't
        // exist yet, both tags stay empty (correct — no orphan cleanup
        // possible on a doc that never had prior state).
        let preUpdateTags: { studioOrgId: string; agencyOrgId: string } = { studioOrgId: '', agencyOrgId: '' };
        try {
          const preSnap = await firestoreGetDoc(jobRef);
          if (preSnap.exists()) {
            const d: any = preSnap.data();
            preUpdateTags = {
              studioOrgId: d.studioOrgId || '',
              agencyOrgId: d.agencyOrgId || '',
            };
          }
        } catch { /* best-effort — leave empty */ }

        await updateDoc(jobRef, updatedPayload);
        // Reconcile studio + agency campaign copies. Re-running on edit
        // handles re-tagging (orphan cleanup) automatically — provided
        // the caller passes the PRE-UPDATE tags (captured above) so
        // syncCampaignCopies can tell what changed. Only run it when
        // something actually changed — a no-op save leaves the copies in sync.
        if (changes.length > 0) {
          try {
            // Carry forward the original create-time fields so the copy
            // mirrors the source. Only include when the value is defined —
            // Firestore rejects setDoc payloads with `undefined` values, so
            // passing missing keys would break the whole sync silently.
            const syncPayload: Record<string, any> = { ...updatedPayload };
            const passthrough = ['fileName', 'sheetName', 'uploadedAt', 'uploadedBy', 'uploadedByName'] as const;
            for (const key of passthrough) {
              const v = (editJob as any)[key];
              if (v !== undefined && v !== null) syncPayload[key] = v;
            }
            await firebase.syncCampaignCopies(targetOrgId, targetJobId, syncPayload, preUpdateTags);
          } catch (err) {
            console.warn('Campaign-copy sync after edit failed (best effort):', err);
          }
        }

        // Linked-track propagation on edit. Two things must reach the
        // linked spots' audiometa when a campaign is edited:
        //   1. The CAMPAIGN NAME (jobCampaign/jobTitle). The audiometa's
        //      campaign context is only otherwise written by JobDetail's
        //      auto-match. Without this, saving a campaign without a name
        //      and later adding it left every linked spot showing the
        //      "Linked" placeholder in the Library forever. Stamped on the
        //      source audiometa + master catalogue + review copies, always
        //      (no copy target required).
        //   2. The full track mirrored into the studio/agency/creator
        //      library (only when those copy targets exist) — same helper
        //      JobDetail uses on auto-match. Because the campaign-name
        //      update is overlaid onto the track data first, those copies
        //      pick up the new name too.
        try {
          const linkedTrackIds = Array.from(new Set(
            stationsWithClearedLinks
              .map((s: any) => s.linkedTrackId)
              .filter((id: any): id is string => typeof id === 'string' && id.length > 0),
          ));
          // Only run the (expensive) fan-out when it can change something. The
          // propagation writes ONLY the campaign name + studio + agency onto the
          // audiometa, so skip it unless one of those header fields changed, OR a
          // NEW track was linked (which needs its context stamped). This makes
          // re-saving an unchanged campaign near-instant.
          const PROPAGATED_HEADER_FIELDS = ['campaign', 'studioOrgId', 'studioName', 'agencyOrgId', 'agencyName'];
          const propagatedHeaderChanged = changes.some(
            (c) => c.kind === 'header_field' && PROPAGATED_HEADER_FIELDS.includes((c as any).field),
          );
          const linksBefore = new Set(
            (Array.isArray(editJob.stations) ? editJob.stations : [])
              .map((s: any) => s.linkedTrackId)
              .filter((id: any) => typeof id === 'string' && id.length > 0),
          );
          const hasNewLinks = linkedTrackIds.some((id) => !linksBefore.has(id));
          const needsPropagation = linkedTrackIds.length > 0 && (propagatedHeaderChanged || hasNewLinks);

          const studioOrgId = ((updatedPayload as any).studioOrgId || '') as string;
          const agencyOrgId = ((updatedPayload as any).agencyOrgId || '') as string;
          const hasCopyTargets = (studioOrgId && studioOrgId !== targetOrgId) ||
                                 (agencyOrgId && agencyOrgId !== targetOrgId);
          // Campaign name to stamp onto each linked track. Kept as the raw
          // string (empty stays empty — matches an un-named campaign) so it
          // mirrors JobDetail's `jobCampaign: job.campaign` write exactly.
          const campaignName = typeof header.campaign === 'string'
            ? header.campaign
            : (((header.campaign as any) || {}).label || '');
          const campaignFieldUpdate: any = { jobCampaign: campaignName, jobTitle: campaignName };

          // Resolve the campaign's studio + agency to their global lookup
          // entries ({id,label,value}) and stamp them onto the audiometa too —
          // exactly like JobDetail's auto-match. Without this, editing a
          // campaign's studio/agency didn't update the Library's Studio /
          // Agency columns on already-linked spots. Resolved ONCE (reused for
          // every track). Overwrites but does not clear when blank — same
          // "campaign wins" rule auto-match uses.
          if (needsPropagation) {
            setSaveStage({ label: 'Updating linked spots…', pct: 60 });
            try {
              const studioName = ((header as any).studioName || '').toString().trim();
              if (studioName) {
                const rs = await firebase.resolveOrCreateLookupEntry('artists-global', {
                  orgId: (header as any).studioOrgId || null,
                  name: studioName,
                });
                if (rs) campaignFieldUpdate.studio = rs;
              }
            } catch (e) { console.warn('Resolve studio for audiometa failed (best effort):', e); }
            try {
              const agencyName = ((header as any).agencyName || '').toString().trim();
              if (agencyName) {
                const ra = await firebase.resolveOrCreateLookupEntry('agency', {
                  orgId: (header as any).agencyOrgId || null,
                  name: agencyName,
                });
                if (ra) campaignFieldUpdate.agency = ra;
              }
            } catch (e) { console.warn('Resolve agency for audiometa failed (best effort):', e); }

            // Process all linked tracks CONCURRENTLY (was sequential — the
            // main cause of the slow save). Each track's writes are
            // independent of the others, so they fan out in parallel; within a
            // track the mirror writes also run together. Every track is
            // isolated in its own try/catch so one failure can't reject the
            // whole batch.
            await Promise.all(linkedTrackIds.map(async (trackId) => {
              try {
                const trackRef = firestoreDoc(
                  firebase.db,
                  getOrgCollectionPath(targetOrgId, 'audiometa'),
                  trackId,
                );
                const trackSnap = await firestoreGetDoc(trackRef);
                if (!trackSnap.exists()) return;

                // Stamp the campaign name onto the source audiometa + its
                // master-catalogue / review-track mirrors, and (when copy
                // targets exist) mirror the full track into the studio /
                // agency / creator libraries — all concurrently. The source
                // updateDoc is the only critical write (its rejection bubbles
                // to the per-track catch); the mirrors are best-effort.
                const writes: Promise<any>[] = [
                  updateDoc(trackRef, campaignFieldUpdate),
                  firebase.syncMasterCatalogue(targetOrgId, trackId, campaignFieldUpdate)
                    .catch((e: any) => console.warn(`syncMasterCatalogue failed for ${trackId} (best effort):`, e)),
                  firebase.syncReviewTrackCopies(targetOrgId, trackId, campaignFieldUpdate)
                    .catch((e: any) => console.warn(`syncReviewTrackCopies failed for ${trackId} (best effort):`, e)),
                ];

                if (hasCopyTargets) {
                  const trackData = { ...(trackSnap.data() || {}), ...campaignFieldUpdate } as any;
                  // Strip copy-marker flags so the destination org's copy
                  // doesn't inherit stale ones from a source that was itself
                  // a copy.
                  delete trackData.isReviewTrack;
                  delete trackData.isDeliveryTrack;
                  delete trackData.isCampaignCopy;
                  writes.push(
                    firebase.syncTrackToCampaignOrgs(
                      targetOrgId,
                      trackId,
                      trackData,
                      {
                        studioOrgId,
                        agencyOrgId,
                        campaignSourceOrgId: targetOrgId,
                      },
                    ).catch((e: any) => console.warn(`syncTrackToCampaignOrgs failed for ${trackId} (best effort):`, e)),
                  );
                }

                await Promise.all(writes);
              } catch (perTrackErr) {
                console.warn(`Linked-track propagation failed for ${trackId} (best effort):`, perTrackErr);
              }
            }));
          }
        } catch (err) {
          console.warn('Linked-track propagation after edit failed (best effort):', err);
        }

        setSaveStage({ label: 'Finishing up…', pct: 90 });

        // History log — reuses the diff computed above for the status
        // decision. Best-effort — a failed write here doesn't block the
        // actual campaign save.
        try {
          await firebase.logCampaignChange(targetOrgId, targetJobId, {
            ...actor,
            actorOrgRole: actorRole,
            changes,
          });
        } catch (err) {
          console.warn('logCampaignChange failed on edit (best effort):', err);
        }

        // PHASE 2 — cascade delete for any already-delivered stations
        // that were removed in this edit. Runs AFTER the main writes
        // + syncCampaignCopies so the studio/agency copies are
        // already up to date (their stations array was replaced by
        // syncCampaignCopies' merge). This helper handles broadcaster
        // copies, delivery record markings, public-link disabling,
        // and audiometa cleanup across all affected orgs.
        // Cascade cleanup for EVERY deleted station (Phase 4). The
        // helper is a no-op internally for stations that were never
        // delivered — delivery-record and broadcaster-copy steps just
        // find nothing to touch — but it still runs audiometa cleanup
        // for their trackIds across studio/agency copies. Without this
        // wider trigger, deleting a draft/active station left copies
        // sitting in studio/agency libraries forever.
        if (deletedStations.length > 0) {
          try {
            const cascadeResult = await firebase.cascadeDeleteStations(
              targetOrgId,
              targetJobId,
              deletedStations.map((s: any) => ({
                id: s.id,
                linkedTrackId: s.linkedTrackId || null,
              })),
            );
            console.log('[cascadeDeleteStations] result:', cascadeResult);
            if (cascadeResult.errors && cascadeResult.errors.length > 0) {
              console.warn('[cascadeDeleteStations] non-fatal errors:', cascadeResult.errors);
            }
          } catch (err) {
            console.error('[cascadeDeleteStations] failed:', err);
            // Best-effort — the main save already succeeded. Operator
            // can re-run by re-editing if the cascade needs another
            // pass.
          }
        }

        // Option A — withdraw the stale/incorrect delivery footprint for
        // re-deliver-flagged stations (broadcaster / media-house changed).
        // Reuses the delete cascade: it soft-marks the station in the old
        // delivery record(s), reduces/deletes the OLD recipient's copy,
        // syncs the public link, and cleans orphaned audiometa in the old
        // org only — the source still references the track (the station
        // was reset to `pending`, not removed), so source/studio/agency
        // copies stay intact. After this the operator clicks Deliver to
        // re-ship to the corrected recipient via the normal flow.
        if (redeliverStations.length > 0) {
          try {
            const redeliverCascade = await firebase.cascadeDeleteStations(
              targetOrgId,
              targetJobId,
              redeliverStations.map((s: any) => ({
                id: s.id,
                linkedTrackId: s.linkedTrackId || null,
              })),
            );
            console.log('[redeliver cascade] result:', redeliverCascade);
            if (redeliverCascade.errors && redeliverCascade.errors.length > 0) {
              console.warn('[redeliver cascade] non-fatal errors:', redeliverCascade.errors);
            }
          } catch (err) {
            console.error('[redeliver cascade] failed:', err);
            // Best-effort — the source station is already reset to
            // pending, so the operator can still re-deliver; a stale
            // old delivery record may linger until a re-edit re-runs this.
          }

          // Now that the stale delivery is withdrawn, recompute the
          // source's station-only delivery counters from live state so the
          // "X/Y downloaded" badge is exact immediately (the incremental
          // counter never decremented on withdrawal). The subsequent
          // Deliver (shareJobWithBroadcasters) increments from this
          // corrected base, keeping the total right. Best-effort.
          await firebase.recomputeSourceDeliveryCount(targetOrgId, targetJobId);
        }

        onSaved?.(editJob.id);
        handleClose();
      } else {
        // Create new job — writes into the current org (creator).
        const jobData = {
          ...header,
          stations: stationsWithClearedLinks,
          stationCount: stationsWithClearedLinks.length,
          linkedCount: 0,
          fileName: file?.name || 'Manual entry',
          sheetName: selectedSheet || '',
          uploadedAt: Date.now(),
          uploadedBy: user?.uid || '',
          uploadedByName: user?.displayName || user?.email || '',
          status: 'draft',
        };

        const jobsRef = firestoreCollection(
          firebase.db,
          getOrgCollectionPath(currentOrgId, 'jobs')
        );
        const docRef = await addDoc(jobsRef, jobData);
        // Copy the new campaign to studio + agency orgs if tagged. Self-skip
        // handled by syncCampaignCopies internally.
        try {
          await firebase.syncCampaignCopies(currentOrgId, docRef.id, jobData);
        } catch (err) {
          console.warn('Campaign-copy on create failed (best effort):', err);
        }

        // History log — creation entry.
        try {
          const changes: CampaignChange[] = [{
            kind: 'campaign_created',
            client: typeof header.client === 'object' && header.client
              ? ((header.client as any).label || (header.client as any).value || null)
              : (header.client || null),
            campaign: header.campaign || null,
          }];
          await firebase.logCampaignChange(currentOrgId, docRef.id, {
            ...actor,
            actorOrgRole: 'source',
            changes,
          });
        } catch (err) {
          console.warn('logCampaignChange failed on create (best effort):', err);
        }

        onSaved?.(docRef.id);
        handleClose();
      }
    } catch (err: any) {
      setError(`Failed to save: ${err.message}`);
      setSaving(false);
      setSaveStage(null);
    }
  };

  // Count linked stations
  const linkedStationCount = editedStations.filter(s => s.stationOrgId).length;

  return (
    <>
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="xl"
      // Prevent accidental loss of in-progress chase-list work — the only
      // close paths are the explicit X (top-right) and Cancel button.
      // Backdrop click + Esc are both disabled.
      backdrop="static"
      keyboard={false}
      style={{
        maxWidth: step === 'review' ? '95vw' : '600px',
        margin: '1.75rem auto',
        transition: 'max-width 0.3s',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button className="close" onClick={handleClose} style={{
            color: 'var(--accent-1-color)', opacity: 1, textShadow: 'none',
            background: 'none', border: 'none', fontSize: '1.75rem', fontWeight: 'normal', cursor: 'pointer',
          }}>
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          {step === 'upload' && 'Upload Chase List'}
          {step === 'select-sheet' && 'Select Sheet'}
          {step === 'parsing' && 'Parsing Chase List...'}
          {step === 'review' && `${isManualMode ? 'New Campaign' : 'Review Parsed Data'} — ${editedStations.length} station${editedStations.length === 1 ? '' : 's'} (${linkedStationCount} linked)`}
          {step === 'saving' && 'Saving...'}
        </span>
      </ModalHeader>

      <ModalBody style={{
        backgroundColor: 'var(--chatbot-color-1)',
        color: '#ffffff',
        padding: '1.5rem',
        maxHeight: step === 'review' ? '75vh' : '60vh',
        overflowY: 'auto',
      }}>
        {error && (
          <div style={{
            marginBottom: '1rem', padding: '0.75rem',
            backgroundColor: 'rgba(255, 80, 80, 0.1)', borderRadius: '6px',
            color: '#ff5050', fontSize: '0.9rem',
          }}>
            {error}
          </div>
        )}

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1.5rem' }}>
              Upload a radio chase list (Excel or CSV). The system will use AI to extract station data for your review.
            </p>
            <div
              style={{
                border: '2px dashed rgba(255, 255, 255, 0.2)', borderRadius: '8px',
                padding: '2rem', cursor: 'pointer', transition: 'border-color 0.2s',
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--accent-1-color)'; }}
              onDragLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                const droppedFile = e.dataTransfer.files[0];
                if (droppedFile && fileInputRef.current) {
                  const dt = new DataTransfer();
                  dt.items.add(droppedFile);
                  fileInputRef.current.files = dt.files;
                  fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }}
            >
              <i className="tim-icons icon-cloud-upload-94" style={{ fontSize: '2rem', color: 'var(--accent-1-color)', marginBottom: '0.75rem', display: 'block' }} />
              <p style={{ color: '#fff', marginBottom: '0.25rem' }}>Drag and drop a chase list, or click to select</p>
              <small style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Supports .xlsx, .xls, .csv</small>
            </div>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFileSelect} />

            {/* OR divider */}
            <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0', gap: '1rem' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />
              <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.85rem' }}>OR</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />
            </div>

            <Button
              className="btn-primary-custom"
              onClick={handleCreateManually}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>
                <i className="tim-icons icon-simple-add mr-2"></i>
                Create Manually
              </span>
            </Button>
          </div>
        )}

        {/* Step 2: Select Sheet(s) */}
        {step === 'select-sheet' && (
          <div>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1rem' }}>
              This file has multiple sheets. Select one or more sheets to combine into a single campaign — the AI will merge station rows from every ticked sheet.
            </p>
            {/* Select-all row — mirrors the "checked" state of the sheet
                list below so it flips to indeterminate/on/off in sync. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.5rem 0.75rem', marginBottom: '0.5rem',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', margin: 0 }}>
                {/* Uses the same Material-UI Checkbox styling as the
                    playlist "Focus for adding tracks" toggle in
                    PlaylistsRow — outlined-blank icon when off, filled
                    icon when on, indeterminate dash icon when a
                    subset is ticked. */}
                <Checkbox
                  checked={selectedSheets.length === sheetNames.length && sheetNames.length > 0}
                  indeterminate={selectedSheets.length > 0 && selectedSheets.length < sheetNames.length}
                  onChange={(e) => {
                    // Capture before setState so React can't null the
                    // event target under us.
                    const isChecked = e.target.checked;
                    setSelectedSheets(isChecked ? [...sheetNames] : []);
                  }}
                  size="small"
                  icon={<CheckBoxOutlineBlankIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />}
                  checkedIcon={<CheckBoxIcon style={{ color: '#fff', fontSize: 20 }} />}
                  indeterminateIcon={<IndeterminateCheckBoxIcon style={{ color: '#fff', fontSize: 20 }} />}
                  inputProps={{ title: 'Select all sheets' }}
                  style={{ marginRight: 0, padding: 0 }}
                />
                <span style={{ fontWeight: 500 }}>Select all</span>
              </label>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                {selectedSheets.length} of {sheetNames.length} selected
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: '340px', overflowY: 'auto' }}>
              {sheetNames.map((name) => {
                const checked = selectedSheets.includes(name);
                return (
                  <label
                    key={name}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.6rem 0.75rem', borderRadius: '4px',
                      backgroundColor: checked ? 'rgba(80, 200, 120, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                      border: `1px solid ${checked ? 'rgba(80, 200, 120, 0.4)' : 'rgba(255, 255, 255, 0.08)'}`,
                      cursor: 'pointer', color: '#fff', margin: 0,
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        // Capture the value BEFORE the state updater
                        // runs — React reuses / clears the event target
                        // asynchronously, so reading `e.target.checked`
                        // inside the updater callback crashes with
                        // "Cannot read properties of null (reading
                        // 'checked')".
                        const isChecked = e.target.checked;
                        setSelectedSheets((prev) => (
                          isChecked
                            ? [...prev, name]
                            : prev.filter((s) => s !== name)
                        ));
                      }}
                      size="small"
                      icon={<CheckBoxOutlineBlankIcon style={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />}
                      checkedIcon={<CheckBoxIcon style={{ color: '#fff', fontSize: 20 }} />}
                      inputProps={{ title: `Include sheet "${name}" in the parse` }}
                      style={{ marginRight: 0, padding: 0 }}
                    />
                    <span>{name}</span>
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <Button
                className="btn-success-custom"
                onClick={() => handleParse(workbook!, selectedSheets)}
                disabled={selectedSheets.length === 0}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>
                  Parse {selectedSheets.length > 1 ? `${selectedSheets.length} Sheets` : 'Selected Sheet'}
                </span>
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Parsing */}
        {step === 'parsing' && (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <Spinner style={{ color: 'var(--accent-1-color)', width: '3rem', height: '3rem' }} />
            <p style={{ color: '#fff', marginTop: '1rem' }}>AI is parsing your chase list...</p>
            <small style={{ color: 'rgba(255, 255, 255, 0.4)' }}>This may take a few seconds</small>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 'review' && parsedData && (
          <div>
            {/* Hidden row warning */}
            {hiddenRowWarning && (
              <div style={{
                marginBottom: '1rem', padding: '0.75rem',
                backgroundColor: 'rgba(255, 180, 50, 0.1)', borderRadius: '6px',
                border: '1px solid rgba(255, 180, 50, 0.3)',
                color: '#ffb432', fontSize: '0.85rem',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
              }}>
                <i className="tim-icons icon-alert-circle-exc" style={{ fontSize: '1rem' }}></i>
                {hiddenRowWarning}
              </div>
            )}
            {/* Header info — editable */}
            <div style={{
              marginBottom: '1.5rem', padding: '1rem',
              backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                <div>
                  <label style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>Client</label>
                  {/* Type-to-filter client picker. Mirrors the station / media
                      owner autocompletes below: `inputValue` is the controlled
                      display text (so MUI's default filtering narrows the list
                      as you type), while the stored `header.client` stays a
                      string (freeform / new client) OR a {label,value} object
                      (matched). Exact-match-on-type auto-links, same as the AI
                      auto-select — resolveClient() normalizes both shapes on save. */}
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={clientOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                    inputValue={(() => {
                      const c = parsedData.header.client;
                      if (!c) return '';
                      return typeof c === 'object' ? ((c as any).label || '') : String(c);
                    })()}
                    onInputChange={(_, value, reason) => {
                      // Ignore MUI's 'reset' (fires on selection / value sync) so
                      // it can't clobber the field; only user typing + clear.
                      if (reason !== 'input' && reason !== 'clear') return;
                      if (reason === 'clear') {
                        setParsedData(prev => prev ? { ...prev, header: { ...prev.header, client: '' } } : prev);
                        return;
                      }
                      // Auto-link on exact (case-insensitive) name match; else keep
                      // the freeform text (a new client resolved/created on save).
                      const v = (value || '').trim().toLowerCase();
                      const exact = v ? clientOptions.find(o => o.label.toLowerCase() === v) : null;
                      setParsedData(prev => prev ? { ...prev, header: { ...prev.header, client: (exact || value) as any } } : prev);
                    }}
                    onChange={(_, newValue) => {
                      setParsedData(prev => {
                        if (!prev) return prev;
                        if (!newValue) {
                          return { ...prev, header: { ...prev.header, client: '' } };
                        }
                        if (typeof newValue === 'string') {
                          const v = newValue.trim().toLowerCase();
                          const exact = clientOptions.find(o => o.label.toLowerCase() === v);
                          return { ...prev, header: { ...prev.header, client: (exact || newValue) as any } };
                        }
                        return { ...prev, header: { ...prev.header, client: newValue as any } };
                      });
                    }}
                    renderInput={(params) => {
                      // Green when the current text matches a known client (either
                      // stored as an object, or a string that resolves) — this is
                      // the AI auto-select indicator, preserved.
                      const c = parsedData.header.client;
                      const str = typeof c === 'object' ? ((c as any)?.label || '') : String(c || '');
                      const matched = str ? clientOptions.find(o => o.label.toLowerCase() === str.toLowerCase()) : null;
                      const isLinked = !!matched || (typeof c === 'object' && !!(c as any)?.label);
                      return (
                        <TextField {...params} variant="outlined" placeholder="Select or type client" className={classes.autocomplete}
                          inputProps={{ ...params.inputProps, style: { color: isLinked ? '#50c878' : '#fff' } }}
                        />
                      );
                    }}
                    renderOption={(option) => (
                      <div style={{ fontSize: '0.8rem', color: '#fff' }}>
                        {typeof option === 'string' ? option : option.label}
                      </div>
                    )}
                    className={classes.autocomplete}
                  />
                </div>
                <div>
                  <label style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>Campaign</label>
                  <Input
                    bsSize="sm"
                    value={parsedData.header.campaign || ''}
                    onChange={(e) => { const v = e?.target?.value ?? ''; setParsedData(prev => prev ? { ...prev, header: { ...prev.header, campaign: v } } : prev); }}
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.85rem' }}
                    placeholder="Campaign name"
                  />
                </div>
                <div>
                  <label style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>Period</label>
                  <Input
                    bsSize="sm"
                    value={parsedData.header.period || ''}
                    onChange={(e) => { const v = e?.target?.value ?? ''; setParsedData(prev => prev ? { ...prev, header: { ...prev.header, period: v } } : prev); }}
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.85rem' }}
                    placeholder="Flight period"
                  />
                </div>
              </div>

              {/* Second row — Studio + Agency. Tagging an org here causes
                  the campaign + auto-matched assets to be copied into that
                  org's library on save / auto-match. Green = matched to a
                  registered org; white = freeform text (no copy will be
                  routed to a non-existent org). */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                <div>
                  <label style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                    Studio
                    {parsedData.header.studioOrgId
                      ? <span style={{ color: '#50c878', marginLeft: '0.4rem' }}>· linked</span>
                      : (parsedData.header.studioName ? <span style={{ color: 'rgba(255,180,50,0.7)', marginLeft: '0.4rem' }}>· not linked</span> : null)}
                  </label>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={studioOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                    /* Controlled inputValue (display text) so MUI filters as you
                       type — studioName is kept in sync by the handlers below,
                       green-linked state comes from studioOrgId. */
                    inputValue={parsedData.header.studioName || ''}
                    onChange={(_, newValue) => {
                      setParsedData(prev => {
                        if (!prev) return prev;
                        if (!newValue) {
                          return { ...prev, header: { ...prev.header, studioOrgId: '', studioName: '' } };
                        }
                        if (typeof newValue === 'string') {
                          // Free-text — clear the org link.
                          return { ...prev, header: { ...prev.header, studioOrgId: '', studioName: newValue } };
                        }
                        return { ...prev, header: { ...prev.header, studioOrgId: newValue.id, studioName: newValue.name } };
                      });
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason !== 'input' && reason !== 'clear') return;
                      // Auto-link on exact (case-insensitive) name match
                      // against the loaded studioOptions — saves the
                      // user a click when they type a studio's full
                      // name. Mirrors the same behaviour on the station
                      // field. If no match, fall through to freeform.
                      const v = (value || '').trim().toLowerCase();
                      const exactMatch = v
                        ? studioOptions.find((o) => (o.name || '').trim().toLowerCase() === v)
                        : null;
                      if (exactMatch) {
                        setParsedData(prev => prev ? {
                          ...prev,
                          header: { ...prev.header, studioOrgId: exactMatch.id, studioName: exactMatch.name },
                        } : prev);
                        return;
                      }
                      // Free-text — clear the org link.
                      setParsedData(prev => prev ? {
                        ...prev,
                        header: { ...prev.header, studioOrgId: '', studioName: value || '' },
                      } : prev);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" placeholder="Select or type studio" className={classes.autocomplete}
                        inputProps={{ ...params.inputProps, style: { color: parsedData.header.studioOrgId ? '#50c878' : '#fff' } }}
                      />
                    )}
                    renderOption={(option) => (
                      <div style={{ fontSize: '0.8rem', color: '#fff' }}>
                        {typeof option === 'string' ? option : option.name}
                      </div>
                    )}
                    className={classes.autocomplete}
                  />
                </div>
                <div>
                  <label style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem', display: 'block', marginBottom: '2px' }}>
                    Agency
                    {parsedData.header.agencyOrgId
                      ? <span style={{ color: '#50c878', marginLeft: '0.4rem' }}>· linked</span>
                      : (parsedData.header.agencyName ? <span style={{ color: 'rgba(255,180,50,0.7)', marginLeft: '0.4rem' }}>· not linked</span> : null)}
                  </label>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={agencyOptions}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                    /* Controlled inputValue (display text) so MUI filters as you
                       type — agencyName is kept in sync by the handlers below,
                       green-linked state comes from agencyOrgId. */
                    inputValue={parsedData.header.agencyName || ''}
                    onChange={(_, newValue) => {
                      setParsedData(prev => {
                        if (!prev) return prev;
                        if (!newValue) {
                          return { ...prev, header: { ...prev.header, agencyOrgId: '', agencyName: '' } };
                        }
                        if (typeof newValue === 'string') {
                          return { ...prev, header: { ...prev.header, agencyOrgId: '', agencyName: newValue } };
                        }
                        return { ...prev, header: { ...prev.header, agencyOrgId: newValue.id, agencyName: newValue.name } };
                      });
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason !== 'input' && reason !== 'clear') return;
                      // Auto-link on exact (case-insensitive) name match
                      // against the loaded agencyOptions — same UX as
                      // the station and studio fields.
                      const v = (value || '').trim().toLowerCase();
                      const exactMatch = v
                        ? agencyOptions.find((o) => (o.name || '').trim().toLowerCase() === v)
                        : null;
                      if (exactMatch) {
                        setParsedData(prev => prev ? {
                          ...prev,
                          header: { ...prev.header, agencyOrgId: exactMatch.id, agencyName: exactMatch.name },
                        } : prev);
                        return;
                      }
                      setParsedData(prev => prev ? {
                        ...prev,
                        header: { ...prev.header, agencyOrgId: '', agencyName: value || '' },
                      } : prev);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" placeholder="Select or type agency" className={classes.autocomplete}
                        inputProps={{ ...params.inputProps, style: { color: parsedData.header.agencyOrgId ? '#50c878' : '#fff' } }}
                      />
                    )}
                    renderOption={(option) => (
                      <div style={{ fontSize: '0.8rem', color: '#fff' }}>
                        {typeof option === 'string' ? option : option.name}
                      </div>
                    )}
                    className={classes.autocomplete}
                  />
                </div>
              </div>
            </div>

            {/* Station table */}
            <div style={{ overflowX: 'auto' }}>
              <Table dark responsive size="sm" style={{ backgroundColor: 'transparent', marginBottom: 0 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <th style={{ border: 'none', padding: '0.5rem', whiteSpace: 'nowrap' }}>Station</th>
                    <th style={{ border: 'none', padding: '0.5rem', whiteSpace: 'nowrap' }}>Media House</th>
                    <th style={{ border: 'none', padding: '0.5rem', whiteSpace: 'nowrap' }}>Language</th>
                    <th style={{ border: 'none', padding: '0.5rem', whiteSpace: 'nowrap' }}>Duration</th>
                    <th style={{ border: 'none', padding: '0.5rem', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                        <span>Flighting Code</span>
                        {renderApplyToAllHeaderToggle('flightingCode')}
                      </div>
                    </th>
                    <th style={{ border: 'none', padding: '0.5rem', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                        <span>Deadline</span>
                        {renderApplyToAllHeaderToggle('materialDeadline')}
                      </div>
                    </th>
                    <th style={{ border: 'none', padding: '0.5rem', whiteSpace: 'nowrap', minWidth: '220px' }}>Description</th>
                    <th style={{ border: 'none', padding: '0.5rem', width: '30px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {editedStations.map((station, idx) => {
                    const codeParts = parseFlightingCode(station.flightingCode);
                    const lockDuration = codeParts.duration !== null;
                    const lockLanguage = codeParts.language !== null;

                    // Compare durations by digit-only normalisation (e.g. `30"` ≡ `30 sec` ≡ `30`)
                    const normaliseDuration = (s: string | undefined) =>
                      (s || '').replace(/\D+/g, '');
                    const durationConflict =
                      lockDuration &&
                      !!station.aiDuration &&
                      normaliseDuration(station.aiDuration) !== normaliseDuration(codeParts.duration || '');

                    // Compare languages by canonical LANGUAGE_OPTIONS value, resolving
                    // abbreviations via LANGUAGE_ALIASES (e.g. "Eng" → "english").
                    const aiLanguageValues = (station.aiLanguage || '')
                      .split(/[,&]+/)
                      .map((l) => l.trim().toLowerCase())
                      .filter(Boolean)
                      .map((l) => {
                        const direct = LANGUAGE_OPTIONS.find(
                          (lo) => lo.label.toLowerCase() === l || lo.value === l,
                        );
                        if (direct) return direct.value;
                        return LANGUAGE_ALIASES[l] || l;
                      });
                    const codeLanguageValue = codeParts.language?.value || '';
                    const languageConflict =
                      lockLanguage &&
                      !!station.aiLanguage &&
                      !aiLanguageValues.includes(codeLanguageValue);

                    // Broadcaster-acceptance check: does the picked station accept
                    // the language(s) on this row? Only fires when (a) we matched
                    // a known broadcaster, (b) the broadcaster has a non-empty
                    // acceptedLanguages whitelist, (c) the row has at least one
                    // language picked. Surface as an inline warning badge — does
                    // not block, just informs.
                    const matchedBroadcaster = station.stationOrgId
                      ? stationOptions.find((s) => s.linkedOrgId === station.stationOrgId)
                      : undefined;
                    const broadcasterAccepted = matchedBroadcaster?.acceptedLanguages || [];
                    const rowLangValues = (station.languageOptions || []).map((o) => o.value);
                    const acceptanceMismatch =
                      !!matchedBroadcaster &&
                      broadcasterAccepted.length > 0 &&
                      rowLangValues.length > 0 &&
                      !rowLangValues.some((v) => broadcasterAccepted.includes(v));

                    const mismatch = mediaOwnerMismatch[idx];
                    return (
                    <React.Fragment key={idx}>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      {/* Station — autocomplete linked to global broadcaster collection.
                          When a media owner is already chosen on this row, the dropdown
                          is filtered to ONLY broadcasters owned by that media owner.
                          Free-typed values still flow through; mismatches surface via
                          the banner if the user types past the filter. */}
                      <td style={{ border: 'none', padding: '0.25rem', minWidth: '180px' }}>
                        {/* Merge status chip + station input in a horizontal
                            flex row so the chip sits INLINE with the field
                            instead of stacking above it. Aligned to
                            flex-start so both the badge and the input's
                            visible border share the same top edge. */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                          {(station as any)._mergeStatus === 'new' && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              // Matches the 28px rendered height of the
                              // Autocomplete input (custom padding via
                              // classes.autocomplete overrides MUI's small
                              // variant default). Keeps badge + input on the
                              // same horizontal band.
                              height: '28px',
                              padding: '0 5px',
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              borderRadius: '3px',
                              backgroundColor: 'rgba(80, 200, 120, 0.18)',
                              color: '#50c878',
                              letterSpacing: '0.3px',
                              flexShrink: 0,
                            }}>NEW</span>
                          )}
                          {(station as any)._mergeStatus === 'updated' && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              height: '28px',
                              padding: '0 5px',
                              fontSize: '0.6rem',
                              fontWeight: 700,
                              borderRadius: '3px',
                              backgroundColor: 'rgba(255, 180, 50, 0.18)',
                              color: '#ffb432',
                              letterSpacing: '0.3px',
                              flexShrink: 0,
                            }}>UPD</span>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={
                            station.mediaOwnerOrgId
                              ? stationOptions.filter(
                                  (s) => s.parentMediaOwnerOrgId === station.mediaOwnerOrgId,
                                )
                              : stationOptions
                          }
                          getOptionLabel={(option: any) => typeof option === 'string' ? option : option.name || ''}
                          inputValue={station.station || ''}
                          onInputChange={(_: any, value: string, reason: string) => {
                            if (reason !== 'input' && reason !== 'clear') return;
                            if (reason === 'clear') {
                              handleEditField(idx, 'station', value);
                              handleEditField(idx, 'stationOrgId', null);
                              handleEditField(idx, 'stationOrgName', null);
                              return;
                            }
                            // Auto-link on exact (case-insensitive) name match
                            // against the filtered options. Saves the user a
                            // click when they type a broadcaster's full name.
                            const filteredOptions = station.mediaOwnerOrgId
                              ? stationOptions.filter((s) => s.parentMediaOwnerOrgId === station.mediaOwnerOrgId)
                              : stationOptions;
                            const v = (value || '').trim().toLowerCase();
                            const exactMatch = v
                              ? filteredOptions.find((s) => (s.name || '').trim().toLowerCase() === v)
                              : null;
                            if (exactMatch) {
                              handleStationOrgSelect(idx, exactMatch);
                              return;
                            }
                            handleEditField(idx, 'station', value);
                            // If the user keeps typing past an earlier
                            // auto-linked exact match, drop the stale link.
                            if (station.stationOrgId) {
                              handleEditField(idx, 'stationOrgId', null);
                              handleEditField(idx, 'stationOrgName', null);
                            }
                          }}
                          onChange={(_: any, newValue: any) => {
                            if (!newValue) {
                              handleEditField(idx, 'station', '');
                              handleEditField(idx, 'stationOrgId', null);
                              handleEditField(idx, 'stationOrgName', null);
                            } else if (typeof newValue === 'string') {
                              handleEditField(idx, 'station', newValue);
                              handleEditField(idx, 'stationOrgId', null);
                              handleEditField(idx, 'stationOrgName', null);
                            } else {
                              handleStationOrgSelect(idx, newValue);
                            }
                          }}
                          renderInput={(params: any) => (
                            <TextField {...params} variant="outlined" className={classes.autocomplete}
                              inputProps={{ ...params.inputProps, style: { color: station.stationOrgId ? '#50c878' : (station.station ? '#ffb432' : '#fff') } }}
                            />
                          )}
                          renderOption={(option: any) => (
                            <div style={{ fontSize: '0.8rem', color: '#fff' }}>
                              {typeof option === 'string' ? option : (
                                <>{option.name} {option.linkedOrgId && <span style={{ color: 'rgba(80,200,120,0.6)', fontSize: '0.65rem', marginLeft: '4px' }}>linked</span>}</>
                              )}
                            </div>
                          )}
                          className={classes.autocomplete}
                        />
                        {/* Unlinked broadcaster flag (Option B): a typed name
                            with no linked in-app org only ever gets an external
                            delivery link — surface it so it's not missed. */}
                        {!station.stationOrgId && !!station.station && (
                          <div style={{ fontSize: '0.6rem', color: '#ffb432', marginTop: '2px', whiteSpace: 'nowrap' }}
                            title="Not linked to an in-app organization — this broadcaster will only receive an external delivery link, not an in-app delivery.">
                            <i className="tim-icons icon-alert-circle-exc" style={{ fontSize: '0.6rem', marginRight: '3px' }} />not linked
                          </div>
                        )}
                          </div>
                        </div>
                      </td>
                      {/* Media Owner — autocomplete linked to global mediaowner collection */}
                      <td style={{ border: 'none', padding: '0.25rem', minWidth: '150px' }}>
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={mediaOwnerOptions}
                          getOptionLabel={(option: any) => typeof option === 'string' ? option : option.name || ''}
                          inputValue={station.mediaOwner || ''}
                          onInputChange={(_: any, value: string, reason: string) => {
                            if (reason !== 'input' && reason !== 'clear') return;
                            if (reason === 'clear') {
                              handleEditField(idx, 'mediaOwner', value);
                              handleEditField(idx, 'mediaOwnerOrgId', null);
                              handleEditField(idx, 'mediaOwnerOrgName', null);
                              return;
                            }
                            // Auto-link on exact (case-insensitive) match.
                            const v = (value || '').trim().toLowerCase();
                            const exactMatch = v
                              ? mediaOwnerOptions.find((m) => (m.name || '').trim().toLowerCase() === v)
                              : null;
                            if (exactMatch) {
                              handleMediaOwnerOrgSelect(idx, exactMatch);
                              return;
                            }
                            handleEditField(idx, 'mediaOwner', value);
                            if (station.mediaOwnerOrgId) {
                              handleEditField(idx, 'mediaOwnerOrgId', null);
                              handleEditField(idx, 'mediaOwnerOrgName', null);
                            }
                          }}
                          onChange={(_: any, newValue: any) => {
                            if (!newValue) {
                              handleEditField(idx, 'mediaOwner', '');
                              handleEditField(idx, 'mediaOwnerOrgId', null);
                              handleEditField(idx, 'mediaOwnerOrgName', null);
                            } else if (typeof newValue === 'string') {
                              handleEditField(idx, 'mediaOwner', newValue);
                              handleEditField(idx, 'mediaOwnerOrgId', null);
                              handleEditField(idx, 'mediaOwnerOrgName', null);
                            } else {
                              handleMediaOwnerOrgSelect(idx, newValue);
                            }
                          }}
                          renderInput={(params: any) => (
                            <TextField {...params} variant="outlined" className={classes.autocomplete}
                              inputProps={{ ...params.inputProps, style: { color: station.mediaOwnerOrgId ? '#50c878' : (station.mediaOwner ? '#ffb432' : '#fff') } }}
                            />
                          )}
                          renderOption={(option: any) => (
                            <div style={{ fontSize: '0.8rem', color: '#fff' }}>
                              {typeof option === 'string' ? option : (
                                <>{option.name} {option.linkedOrgId && <span style={{ color: 'rgba(80,200,120,0.6)', fontSize: '0.65rem', marginLeft: '4px' }}>linked</span>}</>
                              )}
                            </div>
                          )}
                          className={classes.autocomplete}
                        />
                        {/* Unlinked media-house flag (Option B): typed name,
                            no linked org → external link only, no in-app
                            delivery to the media house's library. */}
                        {!station.mediaOwnerOrgId && !!station.mediaOwner && (
                          <div style={{ fontSize: '0.6rem', color: '#ffb432', marginTop: '2px', whiteSpace: 'nowrap' }}
                            title="Not linked to an in-app organization — this media house will only receive an external delivery link, not an in-app delivery.">
                            <i className="tim-icons icon-alert-circle-exc" style={{ fontSize: '0.6rem', marginRight: '3px' }} />not linked
                          </div>
                        )}
                      </td>
                      {/* Language — multi-select from LANGUAGE_OPTIONS, locked when derived from flighting code */}
                      <td style={{ border: 'none', padding: '0.25rem', minWidth: '150px' }}>
                        <div
                          title={
                            lockLanguage
                              ? `Set from flighting code (${codeParts.languageDisplayName || codeParts.language?.label})${
                                  languageConflict ? ` — chase list said "${station.aiLanguage}"` : ''
                                }`
                              : undefined
                          }
                          style={{ position: 'relative' }}
                        >
                          <Autocomplete
                            multiple
                            size="small"
                            disabled={lockLanguage}
                            options={LANGUAGE_OPTIONS}
                            getOptionLabel={(option) => option.label}
                            value={station.languageOptions || []}
                            onChange={(_, value) => handleLanguageChange(idx, value)}
                            renderTags={(value, getTagProps) =>
                              value.map((option, i) => (
                                <Chip key={option.value} label={option.label} {...getTagProps({ index: i })} size="small" />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField {...params} variant="outlined" className={classes.autocomplete} />
                            )}
                            className={classes.autocomplete}
                          />
                          {languageConflict && (
                            <span
                              title={`Chase list said "${station.aiLanguage}", flighting code says "${codeParts.language?.label}"`}
                              style={{
                                position: 'absolute', top: 2, right: 2,
                                fontSize: '0.65rem', color: '#ffb432',
                                background: 'rgba(255, 180, 50, 0.15)',
                                padding: '0 4px', borderRadius: 3,
                              }}
                            >
                              !
                            </span>
                          )}
                        </div>
                        {acceptanceMismatch && (
                          <div
                            title={`${matchedBroadcaster?.name} accepts: ${broadcasterAccepted.join(', ')}`}
                            style={{
                              marginTop: 4,
                              fontSize: '0.7rem',
                              color: '#ffb432',
                              background: 'rgba(255, 180, 50, 0.12)',
                              border: '1px solid rgba(255, 180, 50, 0.3)',
                              borderRadius: 3,
                              padding: '2px 6px',
                              lineHeight: 1.3,
                            }}
                          >
                            ⚠ Station accepts {broadcasterAccepted.join(', ')}
                          </div>
                        )}
                      </td>
                      {/* Duration — locked when derived from flighting code */}
                      <td style={{ border: 'none', padding: '0.25rem' }}>
                        <div
                          title={
                            lockDuration
                              ? `Set from flighting code${
                                  durationConflict ? ` — chase list said "${station.aiDuration}"` : ''
                                }`
                              : undefined
                          }
                          style={{ position: 'relative', maxWidth: '80px' }}
                        >
                          <Input
                            bsSize="sm"
                            value={station.duration}
                            onChange={(e) => handleEditField(idx, 'duration', e.target.value)}
                            disabled={lockDuration}
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.08)',
                              border: 'none',
                              color: '#fff',
                              fontSize: '0.8rem',
                              maxWidth: '80px',
                              opacity: lockDuration ? 0.85 : 1,
                            }}
                          />
                          {durationConflict && (
                            <span
                              title={`Chase list said "${station.aiDuration}", flighting code says "${codeParts.duration}"`}
                              style={{
                                position: 'absolute', top: 2, right: 2,
                                fontSize: '0.65rem', color: '#ffb432',
                                background: 'rgba(255, 180, 50, 0.15)',
                                padding: '0 4px', borderRadius: 3,
                              }}
                            >
                              !
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Flighting Code — text (critical, must be exact). The
                          "apply to all" toggle lives in the column header.
                          When the code carries a Live Read suffix
                          (LR / L_R / L), a purple LR badge sits inline with
                          the input. Computed dynamically from the current
                          input value so the badge appears / disappears as
                          the user edits the code, not just from the flag
                          that was persisted at parse time. */}
                      <td style={{ border: 'none', padding: '0.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Input
                              bsSize="sm"
                              value={station.flightingCode}
                              onChange={(e) => handleEditField(idx, 'flightingCode', e.target.value)}
                              style={{
                                backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', fontSize: '0.8rem',
                                fontFamily: 'monospace',
                                color: station.flightingCode === 'TBA' ? '#ffb432' : 'var(--accent-1-color)',
                              }}
                            />
                          </div>
                          {isLiveReadCode(station.flightingCode) && (
                            <span
                              title="Live Read — read on-air by the DJ; no audio to link"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                // Matches the 28px input rendered height so
                                // the badge shares the same horizontal band.
                                height: '28px',
                                padding: '0 5px',
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                borderRadius: '3px',
                                backgroundColor: 'rgba(140, 100, 220, 0.18)',
                                color: '#b394ff',
                                letterSpacing: '0.3px',
                                flexShrink: 0,
                              }}
                            >LR</span>
                          )}
                        </div>
                      </td>
                      {/* Deadline — text. "apply to all" toggle is in the column header. */}
                      <td style={{ border: 'none', padding: '0.25rem' }}>
                        <Input
                          bsSize="sm"
                          value={station.materialDeadline}
                          onChange={(e) => handleEditField(idx, 'materialDeadline', e.target.value)}
                          style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', fontSize: '0.8rem' }}
                        />
                      </td>
                      {/* Description — spot brief. Cell stays single-line so the
                          row height matches the other inputs; the expand
                          button opens a modal with a proper textarea for the
                          full brief. Hover (native title) gives a quick peek
                          without opening anything. */}
                      <td style={{ border: 'none', padding: '0.25rem', minWidth: '220px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <Input
                            bsSize="sm"
                            type="text"
                            value={(station as any).description || ''}
                            onChange={(e) => handleEditField(idx, 'description', e.target.value)}
                            title={(station as any).description || ''}
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.08)',
                              border: 'none',
                              color: '#fff',
                              fontSize: '0.8rem',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                            }}
                            placeholder="Spot description / brief"
                          />
                          <button
                            type="button"
                            onClick={() => setEditingDescriptionIdx(idx)}
                            title="View / edit full description"
                            style={{
                              background: 'none',
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              color: 'rgba(255, 255, 255, 0.55)',
                              cursor: 'pointer',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '0.8rem',
                              lineHeight: 1,
                              flexShrink: 0,
                            }}
                          >
                            <i className="tim-icons icon-zoom-split" style={{ fontSize: '0.75rem' }} />
                          </button>
                        </div>
                      </td>
                      {/* Remove */}
                      <td style={{ border: 'none', padding: '0.25rem' }}>
                        <button
                          onClick={() => handleRemoveStation(idx)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '1rem', padding: '2px' }}
                          title="Remove station"
                        >
                          &times;
                        </button>
                      </td>
                    </tr>
                    {mismatch && (
                      <tr>
                        <td
                          colSpan={10}
                          style={{
                            background: 'rgba(255, 180, 50, 0.12)',
                            borderTop: '1px solid rgba(255, 180, 50, 0.25)',
                            borderBottom: '1px solid rgba(255, 180, 50, 0.25)',
                            padding: '0.5rem 0.75rem',
                            color: '#ffb432',
                            fontSize: '0.8rem',
                          }}
                        >
                          <span>
                            <strong>{station.stationOrgName || station.station}</strong>{' '}
                            belongs to <strong>{mismatch.parentName}</strong>, not{' '}
                            <strong>{station.mediaOwnerOrgName}</strong> — update?
                          </span>
                          <button
                            type="button"
                            onClick={() => handleAcceptMismatch(idx)}
                            style={{
                              marginLeft: '0.75rem',
                              background: 'rgba(255, 180, 50, 0.25)',
                              border: '1px solid rgba(255, 180, 50, 0.5)',
                              color: '#fff',
                              padding: '2px 10px',
                              borderRadius: '3px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                            }}
                          >
                            Yes, update
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDismissMismatch(idx)}
                            style={{
                              marginLeft: '0.5rem',
                              background: 'transparent',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              color: 'rgba(255, 255, 255, 0.7)',
                              padding: '2px 10px',
                              borderRadius: '3px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Add row / bulk-add buttons */}
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
              <Button
                size="sm"
                className="btn-primary-custom"
                onClick={handleAddRow}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>
                  <i className="tim-icons icon-simple-add mr-1" style={{ fontSize: '0.7rem' }}></i>
                  Add Station
                </span>
              </Button>
              <Button
                size="sm"
                className="btn-primary-custom"
                onClick={() => setShowFilterModal(true)}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>
                  <i className="tim-icons icon-zoom-split mr-1" style={{ fontSize: '0.7rem' }}></i>
                  Add stations by filter
                </span>
              </Button>
            </div>

            {editedStations.length === 0 && (
              <p style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.4)', marginTop: '1rem' }}>
                No stations yet. Add stations manually or re-parse the file.
              </p>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter style={{
        backgroundColor: 'var(--chatbot-color-2)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        justifyContent: step === 'review' ? 'space-between' : 'flex-end',
        padding: '1rem 1.5rem',
        gap: '0.75rem',
        flexWrap: 'wrap',
      }}>
        {/* Staged save progress — full-width row above the buttons while saving.
            Styled to match the app's standard progress bar (DownloadManager). */}
        {saving && saveStage && (
          <div style={{ width: '100%', marginTop: '0.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{saveStage.label}</span>
              <span style={{ color: 'var(--accent-1-color)', fontSize: '0.8rem' }}>{saveStage.pct}%</span>
            </div>
            <Progress
              value={saveStage.pct}
              style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.1)' }}
              barStyle={{ backgroundColor: 'var(--accent-1-color)', transition: 'width 0.4s ease' }}
            />
          </div>
        )}
        {step === 'review' && (
          <>
            {/* Left cluster — parse-adjacent actions. Wrapped together so
                the ModalFooter's space-between layout keeps them next to
                each other on the left while Save/Cancel stay on the right. */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Button
                className="btn-primary-custom"
                onClick={() => { setStep(sheetNames.length > 1 ? 'select-sheet' : 'upload'); setParsedData(null); setEditedStations([]); }}
                size="sm"
              >
                <span style={{ position: 'relative', zIndex: 2 }}>Re-parse</span>
              </Button>
              {/* Merge flow — visible whenever we have a parsed campaign in
                  view. Common uses: (1) add extra stations from a second
                  chase list, (2) enrich existing rows with descriptions
                  from another list. Matches by flighting code; new wins
                  where non-empty. Links preserved on matched rows. */}
              <Button
                className="btn-info-custom"
                onClick={handleMergeButtonClick}
                size="sm"
                title="Upload another chase list and merge it with the current campaign"
              >
                <span style={{ position: 'relative', zIndex: 2 }}>Merge chase list</span>
              </Button>
              <input
                ref={mergeFileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleMergeFileSelect}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button
                className="btn-success-custom"
                onClick={handleSave}
                disabled={saving || editedStations.length === 0}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>
                  {saving ? (<><Spinner size="sm" className="mr-2" /> Saving...</>) : `Save Campaign (${editedStations.length} stations)`}
                </span>
              </Button>
              <Button className="btn-danger-custom" onClick={handleClose}>
                <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
              </Button>
            </div>
          </>
        )}
        {step !== 'review' && step !== 'parsing' && (
          <Button className="btn-info-custom" onClick={handleClose}>
            <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
          </Button>
        )}
      </ModalFooter>
    </Modal>

    {/* Bulk "Add stations by filter" picker */}
    <Modal
      isOpen={showFilterModal}
      toggle={() => setShowFilterModal(false)}
      size="md"
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={() => setShowFilterModal(false)}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={() => setShowFilterModal(false)}
            style={{
              color: 'var(--accent-1-color)', opacity: 1, textShadow: 'none',
              background: 'none', border: 'none', fontSize: '1.75rem', fontWeight: 'normal', cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>Add stations by filter</span>
      </ModalHeader>
      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
        }}
      >
        <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Filter by province and subtype to bulk-add broadcasters. Duplicates are skipped.
        </p>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>
            Provinces
          </label>
          <Autocomplete
            multiple
            size="small"
            options={FILTER_PROVINCES}
            value={filterProvinces}
            onChange={(_, value) => setFilterProvinces(value as string[])}
            renderTags={(value, getTagProps) =>
              value.map((p, i) => (
                <Chip key={p} label={p} {...getTagProps({ index: i })} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" className={classes.autocomplete} placeholder="Any province" />
            )}
            className={classes.autocomplete}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>
            Sub-types
          </label>
          <Autocomplete
            multiple
            size="small"
            options={FILTER_SUBTYPES}
            getOptionLabel={(o) => o.label}
            value={FILTER_SUBTYPES.filter((o) => filterSubTypes.includes(o.value))}
            onChange={(_, value) => setFilterSubTypes(value.map((v) => v.value))}
            renderTags={(value, getTagProps) =>
              value.map((o, i) => (
                <Chip key={o.value} label={o.label} {...getTagProps({ index: i })} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" className={classes.autocomplete} placeholder="Any sub-type" />
            )}
            className={classes.autocomplete}
          />
        </div>

        <div style={{ color: '#fff', fontSize: '0.85rem', padding: '0.5rem', background: 'rgba(255,255,255,0.06)', borderRadius: 4 }}>
          {filterProvinces.length === 0 && filterSubTypes.length === 0 ? (
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Select at least one filter to see matches.</span>
          ) : (
            <span>
              <strong>{bulkMatchCount}</strong> broadcaster{bulkMatchCount === 1 ? '' : 's'} match
              {bulkMatchCount === 1 ? 'es' : ''} this filter.
            </span>
          )}
        </div>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Button
          size="sm"
          color="secondary"
          onClick={() => {
            setFilterProvinces([]);
            setFilterSubTypes([]);
            setShowFilterModal(false);
          }}
        >
          <span>Cancel</span>
        </Button>
        <Button
          size="sm"
          className="btn-primary-custom"
          onClick={handleApplyBulkFilter}
          disabled={bulkMatchCount === 0}
        >
          <span>Add {bulkMatchCount > 0 ? bulkMatchCount : ''} station{bulkMatchCount === 1 ? '' : 's'}</span>
        </Button>
      </ModalFooter>
    </Modal>

    {/* Per-row description view / edit. Opens from the expand icon on the
        description cell — the cell itself stays single-line to keep the
        row height consistent with other inputs. Edits flow through the
        normal handleEditField path, so "apply to all" / sticky sync rules
        (if ever added for description) would work the same way. */}

    {/* Phase 2 cascade-delete confirmation modal. Rendered whenever
        `deleteConfirm` is non-null. Both Continue and Cancel resolve
        the awaiting handleSave promise so the flow proceeds or aborts.
        Backdrop click is disabled — the user must make an explicit
        choice to avoid an accidental cancel of an in-progress save. */}
    <Modal
      isOpen={deleteConfirm !== null}
      backdrop="static"
      keyboard={false}
      size="lg"
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <span style={{ color: '#fff' }}>
          <i
            className="tim-icons icon-alert-circle-exc mr-2"
            style={{ color: '#ff5050' }}
          />
          Remove {deleteConfirm?.stations.length ?? 0} delivered station
          {(deleteConfirm?.stations.length ?? 0) === 1 ? '' : 's'}?
        </span>
      </ModalHeader>
      <ModalBody style={{ backgroundColor: 'var(--chatbot-color-1)', color: '#fff' }}>
        <p style={{ marginBottom: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>
          You are about to remove the following station
          {(deleteConfirm?.stations.length ?? 0) === 1 ? '' : 's'} — already
          delivered — from this campaign:
        </p>
        <div
          style={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
            padding: '0.5rem 0.75rem',
            maxHeight: 200,
            overflowY: 'auto',
            marginBottom: '1rem',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
          }}
        >
          {(deleteConfirm?.stations || []).map((s: any, i: number) => (
            <div key={s.id || i} style={{ padding: '2px 0' }}>
              <span style={{ color: '#fff' }}>{s.station || '(no station name)'}</span>
              <span style={{ color: 'rgba(255,255,255,0.4)', margin: '0 0.5rem' }}>—</span>
              <span style={{ color: 'var(--accent-1-color)' }}>
                {s.flightingCode || '(no code)'}
              </span>
            </div>
          ))}
        </div>
        <p style={{ marginBottom: '0.5rem', color: 'rgba(255,255,255,0.85)' }}>
          This will:
        </p>
        <ul
          style={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: '0.85rem',
            paddingLeft: '1.25rem',
            marginBottom: '1rem',
          }}
        >
          <li>Remove them from every studio, agency, broadcaster and media-house campaign copy</li>
          <li>Delete their audio tracks from those orgs' libraries when no other campaign references the track</li>
          <li>Mark the affected delivery record(s) as deleted (audit trail preserved)</li>
          <li>Disable the external delivery link when every station in the delivery is gone</li>
        </ul>
        <p
          style={{
            marginBottom: 0,
            color: '#ff5050',
            fontWeight: 500,
            fontSize: '0.85rem',
          }}
        >
          This cannot be undone.
        </p>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Button
          size="sm"
          color="secondary"
          onClick={() => {
            deleteConfirm?.resolve(false);
            setDeleteConfirm(null);
          }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="btn-primary-custom"
          style={{ backgroundColor: '#ff5050', borderColor: '#ff5050' }}
          onClick={() => {
            deleteConfirm?.resolve(true);
            setDeleteConfirm(null);
          }}
        >
          <span>Remove &amp; Save</span>
        </Button>
      </ModalFooter>
    </Modal>

    {/* Missing-agency reminder. Fires on NEW campaign saves whenever
        the agency field is blank (regardless of studio). Two choices:
        Add (aborts save so user can fill in) or Skip (proceeds without
        agency). Backdrop click disabled — explicit choice required so
        the save state doesn't get stuck. */}
    <Modal
      isOpen={studioAgencyConfirm !== null}
      backdrop="static"
      keyboard={false}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <span style={{ color: '#fff' }}>
          <i
            className="tim-icons icon-alert-circle-exc mr-2"
            style={{ color: '#ffb432' }}
          />
          Agency not set
        </span>
      </ModalHeader>
      <ModalBody style={{ backgroundColor: 'var(--chatbot-color-1)', color: '#fff' }}>
        <p style={{ marginBottom: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>
          You haven't tagged an agency on this campaign. Agencies get a linked
          copy of the campaign and its audio in their own org.
        </p>
        <p style={{ marginBottom: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
          You can add one now, or skip and save without an agency.
        </p>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Button
          size="sm"
          color="secondary"
          onClick={() => {
            // Skip — proceed with save even though both fields are blank.
            studioAgencyConfirm?.resolve(true);
            setStudioAgencyConfirm(null);
          }}
        >
          Skip
        </Button>
        <Button
          size="sm"
          className="btn-primary-custom"
          onClick={() => {
            // Add — abort save so the user can fill in a field.
            studioAgencyConfirm?.resolve(false);
            setStudioAgencyConfirm(null);
          }}
        >
          <span>Add</span>
        </Button>
      </ModalFooter>
    </Modal>

    {/* Unlinked-orgs save warning (create + edit). Fires when a station
        has a broadcaster / media-house NAME but no linked in-app org.
        Such recipients only get an external delivery link — no in-app
        delivery / library copy. Proceed ships anyway (external-only is a
        valid path); Go back aborts so the user can link the org. */}
    <Modal
      isOpen={unlinkedConfirm !== null}
      backdrop="static"
      keyboard={false}
      size="lg"
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <span style={{ color: '#fff' }}>
          <i className="tim-icons icon-alert-circle-exc mr-2" style={{ color: '#ffb432' }} />
          {unlinkedConfirm?.rows.length ?? 0} recipient
          {(unlinkedConfirm?.rows.length ?? 0) === 1 ? '' : 's'} not linked
        </span>
      </ModalHeader>
      <ModalBody style={{ backgroundColor: 'var(--chatbot-color-1)', color: '#fff' }}>
        <p style={{ marginBottom: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>
          The following broadcaster / media-house name
          {(unlinkedConfirm?.rows.length ?? 0) === 1 ? ' is' : 's are'} not linked to an in-app
          organization:
        </p>
        <div
          style={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
            padding: '0.5rem 0.75rem',
            maxHeight: 200,
            overflowY: 'auto',
            marginBottom: '1rem',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
          }}
        >
          {(unlinkedConfirm?.rows || []).map((r, i) => (
            <div key={i} style={{ padding: '2px 0' }}>
              {r.missingStation && (
                <span style={{ color: '#ffb432' }}>{r.station || '(no station name)'} <span style={{ color: 'rgba(255,255,255,0.4)' }}>(broadcaster)</span></span>
              )}
              {r.missingStation && r.missingMediaOwner && (
                <span style={{ color: 'rgba(255,255,255,0.4)', margin: '0 0.4rem' }}>·</span>
              )}
              {r.missingMediaOwner && (
                <span style={{ color: '#ffb432' }}>{r.mediaOwner || '(no media house name)'} <span style={{ color: 'rgba(255,255,255,0.4)' }}>(media house)</span></span>
              )}
            </div>
          ))}
        </div>
        <p style={{ marginBottom: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem' }}>
          Unlinked recipients only receive an <strong>external delivery link</strong> — no in-app
          delivery happens and the campaign will <strong>not</strong> appear in their library. Link
          them to the correct organization to deliver in-app.
        </p>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Button
          size="sm"
          className="btn-primary-custom"
          onClick={() => {
            // Go back — abort the save so the user can link the orgs.
            unlinkedConfirm?.resolve(false);
            setUnlinkedConfirm(null);
          }}
        >
          <span>Go back &amp; link</span>
        </Button>
        <Button
          size="sm"
          color="secondary"
          onClick={() => {
            // Proceed — external-link-only delivery is intentional.
            unlinkedConfirm?.resolve(true);
            setUnlinkedConfirm(null);
          }}
        >
          Save anyway
        </Button>
      </ModalFooter>
    </Modal>

    {/* Re-deliver-corrected-station confirmation (Option A). Fires on edit
        saves when an already-delivered station's broadcaster / media-house
        org link changed. Reset & Save withdraws the stale delivery and
        resets those stations to pending for re-delivery; Cancel aborts. */}
    <Modal
      isOpen={redeliverConfirm !== null}
      backdrop="static"
      keyboard={false}
      size="lg"
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <span style={{ color: '#fff' }}>
          <i className="tim-icons icon-refresh-01 mr-2" style={{ color: '#ffb432' }} />
          Re-deliver {redeliverConfirm?.stations.length ?? 0} corrected station
          {(redeliverConfirm?.stations.length ?? 0) === 1 ? '' : 's'}?
        </span>
      </ModalHeader>
      <ModalBody style={{ backgroundColor: 'var(--chatbot-color-1)', color: '#fff' }}>
        <p style={{ marginBottom: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>
          The broadcaster / media house changed on the following already-delivered station
          {(redeliverConfirm?.stations.length ?? 0) === 1 ? '' : 's'}:
        </p>
        <div
          style={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
            padding: '0.5rem 0.75rem',
            maxHeight: 200,
            overflowY: 'auto',
            marginBottom: '1rem',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
          }}
        >
          {(redeliverConfirm?.stations || []).map((s: any, i: number) => (
            <div key={s.id || i} style={{ padding: '2px 0' }}>
              <span style={{ color: '#fff' }}>{s.station || '(no station name)'}</span>
              {s.mediaOwner && (
                <>
                  <span style={{ color: 'rgba(255,255,255,0.4)', margin: '0 0.5rem' }}>·</span>
                  <span style={{ color: 'rgba(255,255,255,0.6)' }}>{s.mediaOwner}</span>
                </>
              )}
            </div>
          ))}
        </div>
        <p style={{ marginBottom: '0.5rem', color: 'rgba(255,255,255,0.85)' }}>
          Saving will:
        </p>
        <ul
          style={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: '0.85rem',
            paddingLeft: '1.25rem',
            marginBottom: '1rem',
          }}
        >
          <li>Withdraw the previous (incorrect) delivery — removing the spot from the old recipient's library</li>
          <li>Reset {(redeliverConfirm?.stations.length ?? 0) === 1 ? 'the station' : 'those stations'} to <strong>pending</strong></li>
          <li>Keep every other delivered station exactly as-is</li>
        </ul>
        <p style={{ marginBottom: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
          After saving, click <strong>Deliver</strong> on the campaign to send to the corrected recipient.
        </p>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Button
          size="sm"
          color="secondary"
          onClick={() => {
            redeliverConfirm?.resolve(false);
            setRedeliverConfirm(null);
          }}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="btn-primary-custom"
          onClick={() => {
            redeliverConfirm?.resolve(true);
            setRedeliverConfirm(null);
          }}
        >
          <span>Reset &amp; Save</span>
        </Button>
      </ModalFooter>
    </Modal>

    <Modal
      isOpen={editingDescriptionIdx !== null}
      toggle={() => setEditingDescriptionIdx(null)}
      size="lg"
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={() => setEditingDescriptionIdx(null)}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={() => setEditingDescriptionIdx(null)}
            style={{
              color: 'var(--accent-1-color)', opacity: 1, textShadow: 'none',
              background: 'none', border: 'none', fontSize: '1.75rem', fontWeight: 'normal', cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          Spot description
          {editingDescriptionIdx !== null && editedStations[editingDescriptionIdx]?.station && (
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
              — {editedStations[editingDescriptionIdx].station}
            </span>
          )}
        </span>
      </ModalHeader>
      <ModalBody style={{ backgroundColor: 'var(--chatbot-color-1)', color: '#fff' }}>
        <Input
          type="textarea"
          rows={10}
          autoFocus
          value={editingDescriptionIdx !== null ? ((editedStations[editingDescriptionIdx] as any)?.description || '') : ''}
          onChange={(e) => {
            if (editingDescriptionIdx !== null) {
              handleEditField(editingDescriptionIdx, 'description', e.target.value);
            }
          }}
          style={{
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff',
            fontSize: '0.9rem',
            resize: 'vertical',
            minHeight: '200px',
            whiteSpace: 'pre-wrap',
          }}
          placeholder="Spot description / brief"
        />
        <small style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginTop: '0.5rem' }}>
          Changes save automatically. This text is copied into the matched track's notes on auto-match and shown to reviewers.
        </small>
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Button
          size="sm"
          className="btn-primary-custom"
          onClick={() => setEditingDescriptionIdx(null)}
        >
          <span>Done</span>
        </Button>
      </ModalFooter>
    </Modal>
    </>
  );
};

export default ChaseListUploader;
